<template>
  <NcrmRecordLayout :left-lg-cols="3" :main-lg-cols-both="7" :right-lg-cols="2">
    <template #header>
      <v-breadcrumbs
        :items="breadcrumbItems"
        density="comfortable"
        class="ncrm-contract-details__breadcrumb pa-0 mb-1"
      />
      <NcrmRecordHeader
        class="ncrm-contract-details__record-header"
        :title="recordTitle"
        :subtitle="recordSubtitle"
        show-back-button
        :back-aria-label="$t('app.actions.back')"
        @back-click="goBack"
      >
        <template #below>
          <div class="ncrm-contract-details__header-strip">
            <v-menu
              v-model="statusMenu"
              location="bottom start"
              offset="8"
              :close-on-content-click="false"
              content-class="ncrm-contract-details__status-menu"
            >
              <template #activator="{ props: menuProps }">
                <v-btn
                  v-bind="menuProps"
                  variant="outlined"
                  size="small"
                  class="ncrm-contract-details__status-pill"
                  :disabled="loading || statusSaving || !normalizedContractId || !contract"
                  :aria-label="$t('ncrm.contracts.details.header.changeStatus')"
                >
                  <span
                    class="ncrm-contract-details__status-dot"
                    :class="statusToneClass(contract?.status)"
                    aria-hidden="true"
                  />
                  <span class="ncrm-contract-details__status-pill-text">
                    {{ statusLabel(contract?.status) }}
                  </span>
                  <span class="ncrm-contract-details__status-pill-end">
                    <v-progress-circular
                      v-if="statusSaving"
                      indeterminate
                      size="18"
                      width="2"
                      color="primary"
                    />
                    <v-icon v-else size="18" class="text-medium-emphasis">mdi-chevron-down</v-icon>
                  </span>
                </v-btn>
              </template>
              <v-list density="comfortable" class="py-1">
                <v-list-subheader>
                  {{ $t("ncrm.contracts.details.header.changeStatus") }}
                </v-list-subheader>
                <v-list-item
                  v-for="option in contractStatusOptions"
                  :key="`contract-status-${option.value}`"
                  :active="option.value === normalizedContractStatus"
                  class="ncrm-contract-details__status-option"
                  :disabled="option.disabled || statusSaving"
                  @click="updateContractStatus(option.value)"
                >
                  <template #prepend>
                    <span
                      class="ncrm-contract-details__status-dot"
                      :class="statusToneClass(option.value)"
                      aria-hidden="true"
                    />
                  </template>
                  <v-list-item-title>{{ option.label }}</v-list-item-title>
                  <v-list-item-subtitle v-if="option.hint">
                    {{ option.hint }}
                  </v-list-item-subtitle>
                  <template #append>
                    <v-icon
                      v-if="option.value === normalizedContractStatus"
                      size="18"
                      color="primary"
                    >
                      mdi-check
                    </v-icon>
                  </template>
                </v-list-item>
              </v-list>
            </v-menu>
            <div class="ncrm-contract-details__header-pill ncrm-contract-details__header-pill--onboarding">
              <v-icon size="16" class="ncrm-contract-details__header-pill-icon">mdi-rocket-launch-outline</v-icon>
              <span class="ncrm-contract-details__header-pill-meta">
                {{ $t("ncrm.contracts.details.context.onboardings") }}:
              </span>
              <span class="ncrm-contract-details__header-pill-value">
                {{ formatNumber(onboardingItems.length) }}
              </span>
            </div>
            <div class="ncrm-contract-details__header-pill ncrm-contract-details__header-pill--financial">
              <v-icon size="16" class="ncrm-contract-details__header-pill-icon">mdi-cash-multiple</v-icon>
              <span class="ncrm-contract-details__header-pill-meta">
                {{ $t("ncrm.contracts.details.context.totalAmount") }}:
              </span>
              <span class="ncrm-contract-details__header-pill-value">
                {{ formatMoney(contract?.total_amount, contract?.currency_code) }}
              </span>
            </div>
            <div class="ncrm-contract-details__header-pill ncrm-contract-details__header-pill--schedule">
              <v-icon size="16" class="ncrm-contract-details__header-pill-icon">mdi-calendar-clock-outline</v-icon>
              <span class="ncrm-contract-details__header-pill-meta">
                {{ $t("ncrm.contracts.details.payments.nextDueAt") }}:
              </span>
              <span class="ncrm-contract-details__header-pill-value">
                {{ formatDate(paymentSummary.next_due_at) }}
              </span>
            </div>
            <div
              v-if="companyRoute || personRoute || hasOnboardingEntries || dealRoute"
              class="ncrm-contract-details__header-nav-group"
            >
              <!-- Company hover card -->
              <v-menu v-if="companyRoute" open-on-hover :open-delay="180" :close-delay="120" location="bottom start" offset="6">
                <template #activator="{ props: hoverProps }">
                  <v-btn v-bind="hoverProps" size="small" variant="outlined" color="primary" @click="openCompany">
                    <v-icon start>mdi-domain</v-icon>
                    {{ $t("ncrm.contracts.actions.openCompany") }}
                  </v-btn>
                </template>
                <v-card class="ncrm-contract-details__hover-card" width="240" elevation="4">
                  <v-card-text class="pa-3">
                    <div class="d-flex align-center ga-2 mb-1">
                      <v-icon size="18" color="primary">mdi-domain</v-icon>
                      <span class="ncrm-contract-details__hover-card-title">{{ contract?.company_name || "—" }}</span>
                    </div>
                    <div class="ncrm-contract-details__hover-card-hint">{{ $t("ncrm.contracts.hover.clickToOpen") }}</div>
                  </v-card-text>
                </v-card>
              </v-menu>
              <!-- Person hover card -->
              <v-menu v-if="personRoute" open-on-hover :open-delay="180" :close-delay="120" location="bottom start" offset="6">
                <template #activator="{ props: hoverProps }">
                  <v-btn v-bind="hoverProps" size="small" variant="outlined" color="primary" @click="openPerson">
                    <v-icon start>mdi-account</v-icon>
                    {{ $t("ncrm.contracts.actions.openPerson") }}
                  </v-btn>
                </template>
                <v-card class="ncrm-contract-details__hover-card" width="240" elevation="4">
                  <v-card-text class="pa-3">
                    <div class="d-flex align-center ga-2 mb-1">
                      <v-icon size="18" color="primary">mdi-account</v-icon>
                      <span class="ncrm-contract-details__hover-card-title">{{ contract?.person_name || "—" }}</span>
                    </div>
                    <div v-if="contract?.company_name" class="ncrm-contract-details__hover-card-sub mb-1">
                      <v-icon size="13" class="mr-1">mdi-domain</v-icon>{{ contract.company_name }}
                    </div>
                    <div class="ncrm-contract-details__hover-card-hint">{{ $t("ncrm.contracts.hover.clickToOpen") }}</div>
                  </v-card-text>
                </v-card>
              </v-menu>
              <!-- Onboarding button (no hover card — count shown in header pill) -->
              <v-btn
                v-if="hasOnboardingEntries"
                size="small"
                variant="outlined"
                color="primary"
                @click="openOnboardingShortcut"
              >
                <v-icon start>mdi-rocket-launch-outline</v-icon>
                {{ $t("ncrm.contracts.actions.openOnboarding") }}
              </v-btn>
              <!-- Deal hover card -->
              <v-menu v-if="dealRoute" open-on-hover :open-delay="180" :close-delay="120" location="bottom start" offset="6">
                <template #activator="{ props: hoverProps }">
                  <v-btn v-bind="hoverProps" size="small" variant="outlined" color="primary" @click="openDeal">
                    <v-icon start>mdi-handshake-outline</v-icon>
                    {{ $t("ncrm.contracts.actions.openDeal") }}
                  </v-btn>
                </template>
                <v-card class="ncrm-contract-details__hover-card" width="240" elevation="4">
                  <v-card-text class="pa-3">
                    <div class="d-flex align-center ga-2 mb-1">
                      <v-icon size="18" color="primary">mdi-handshake-outline</v-icon>
                      <span class="ncrm-contract-details__hover-card-title">{{ $t("ncrm.contracts.hover.deal") }}</span>
                    </div>
                    <div v-if="contract?.company_name" class="ncrm-contract-details__hover-card-sub mb-1">
                      <v-icon size="13" class="mr-1">mdi-domain</v-icon>{{ contract.company_name }}
                    </div>
                    <div class="ncrm-contract-details__hover-card-hint">{{ $t("ncrm.contracts.hover.clickToOpen") }}</div>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
          </div>
        </template>
        <template #actions>
          <div class="ncrm-contract-details__header-actions">
            <v-btn
              color="primary"
              variant="text"
              :disabled="!normalizedContractId"
              @click="openCreateBillingPlanDialog"
            >
              <v-icon start>mdi-receipt-text-plus-outline</v-icon>
              {{ $t("ncrm.contracts.actions.addBillingPlan") }}
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              :disabled="!normalizedContractId"
              @click="openRecordPaymentDialog"
            >
              <v-icon start>mdi-cash-plus</v-icon>
              {{ $t("ncrm.contracts.actions.addPayment") }}
            </v-btn>
            <NcrmContractEmailAction :contract="contract" :contract-id="normalizedContractId" :disabled="loading || !normalizedContractId" @notify="onMailjetNotify" @sent="handleContractEmailSent" />
            <v-tooltip location="top">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  icon="mdi-refresh"
                  variant="text"
                  color="primary"
                  size="small"
                  :loading="loading"
                  :aria-label="$t('ncrm.contracts.actions.refresh')"
                  @click="loadContract"
                />
              </template>
              <span>{{ $t("ncrm.contracts.actions.refresh") }}</span>
            </v-tooltip>
            <v-tooltip location="top">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  icon="mdi-delete-outline"
                  variant="text"
                  color="error"
                  size="small"
                  :disabled="!normalizedContractId || loading || deleteDialog.loading"
                  :aria-label="$t('ncrm.contracts.actions.deleteContract')"
                  @click="openDeleteContractDialog"
                />
              </template>
              <span>{{ $t("ncrm.contracts.actions.deleteContract") }}</span>
            </v-tooltip>
          </div>
        </template>
      </NcrmRecordHeader>
    </template>
    <template #left>
      <v-card variant="flat" class="ncrm-contract-details__panel-card ncrm-contract-details__panel-card--context pa-4">
        <div class="ncrm-contract-details__panel-title ncrm-contract-details__panel-title--context mb-3">
          <v-icon size="15" class="ncrm-contract-details__panel-title-icon">mdi-file-document-outline</v-icon>
          {{ $t("ncrm.contracts.details.context.detailsTitle") }}
        </div>
        <div v-if="loading" class="ncrm-contract-details__skeleton-stack">
          <div v-for="n in 5" :key="n" class="ncrm-contract-details__skeleton-row">
            <v-skeleton-loader type="text" width="60%" />
            <v-skeleton-loader type="text" width="90%" />
          </div>
        </div>
        <div v-else class="ncrm-contract-details__side-stack">
          <NcrmContractSideEditableRow
            :label="$t('ncrm.contracts.details.context.owner')"
            icon="mdi-account-tie-outline"
            :editing="inlineEdit.field === 'owner_user_id'"
            @start="startOwnerEdit"
          >
            <template #editor>
              <v-autocomplete
                v-model="inlineEdit.value"
                :items="ownerOptions"
                item-title="label"
                item-value="id"
                density="compact"
                hide-details
                autofocus
                menu-icon=""
                :loading="inlineEdit.saving || ownerLoading"
                :no-data-text="$t('ncrm.contracts.details.context.ownerNoOptions')"
                class="mt-1"
                @click.stop
                @keydown.esc="cancelInlineEdit"
              >
                <template #append-inner>
                  <v-btn icon size="x-small" variant="text" color="success" :loading="inlineEdit.saving" @click.stop="saveInlineEdit">
                    <v-icon size="14">mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon size="x-small" variant="text" @click.stop="cancelInlineEdit">
                    <v-icon size="14">mdi-close</v-icon>
                  </v-btn>
                </template>
              </v-autocomplete>
            </template>
            {{ valueOrDash(contract?.owner_name) }}
          </NcrmContractSideEditableRow>
          <NcrmContractSideEditableRow
            :label="$t('ncrm.contracts.details.context.person')"
            icon="mdi-account-outline"
            :editing="inlineEdit.field === 'ncrm_person_id'"
            @start="startPersonEdit"
          >
            <template #editor>
              <v-autocomplete
                v-model="inlineEdit.value"
                :items="personOptions"
                item-title="label"
                item-value="id"
                density="compact"
                hide-details
                clearable
                autofocus
                menu-icon=""
                :loading="inlineEdit.saving || personLoading"
                :no-data-text="$t('ncrm.contracts.details.context.personNoOptions')"
                :placeholder="$t('ncrm.contracts.details.context.personSearch')"
                class="mt-1"
                @click.stop
                @keydown.esc="cancelInlineEdit"
                @update:search="onPersonSearch"
              >
                <template #append-inner>
                  <v-btn icon size="x-small" variant="text" color="success" :loading="inlineEdit.saving" @click.stop="saveInlineEdit">
                    <v-icon size="14">mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon size="x-small" variant="text" @click.stop="cancelInlineEdit">
                    <v-icon size="14">mdi-close</v-icon>
                  </v-btn>
                </template>
              </v-autocomplete>
            </template>
            <template v-if="contract?.person_name">{{ contract.person_name }}</template>
            <span v-else class="ncrm-contract-details__placeholder-text">
              <v-icon size="13" class="ncrm-contract-details__placeholder-action-icon">mdi-plus-circle-outline</v-icon>
              {{ $t("ncrm.contracts.details.context.addPerson") }}
            </span>
          </NcrmContractSideEditableRow>
          <div class="ncrm-contract-details__side-row">
            <div class="ncrm-contract-details__side-label">
              <v-icon size="14" class="ncrm-contract-details__row-icon">mdi-calendar-outline</v-icon>
              {{ $t("ncrm.contracts.details.context.contractDate") }}
            </div>
            <div class="ncrm-contract-details__side-value">{{ formatDate(contract?.contract_date) }}</div>
          </div>
          <NcrmContractSideEditableRow
            :label="$t('ncrm.contracts.details.context.period')"
            icon="mdi-calendar-range-outline"
            :editing="inlineEdit.field === 'end_date'"
            @start="startInlineEdit('end_date')"
          >
            <template #editor>
              <v-text-field
                v-model="inlineEdit.value"
                type="date"
                density="compact"
                hide-details
                autofocus
                class="mt-1"
                :loading="inlineEdit.saving"
                @click.stop
                @keydown.enter="saveInlineEdit"
                @keydown.esc="cancelInlineEdit"
              >
                <template #append-inner>
                  <v-btn icon size="x-small" variant="text" color="success" :loading="inlineEdit.saving" @click.stop="saveInlineEdit">
                    <v-icon size="14">mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon size="x-small" variant="text" @click.stop="cancelInlineEdit">
                    <v-icon size="14">mdi-close</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </template>
            <template v-if="contract?.start_date">
              {{ formatDate(contract.start_date) }}
              <span class="ncrm-contract-details__side-value--muted"> → </span>
              <template v-if="contract?.end_date">{{ formatDate(contract.end_date) }}</template>
              <span v-else class="ncrm-contract-details__side-value--muted">{{ $t("ncrm.contracts.details.context.periodOpen") }}</span>
            </template>
            <span v-else class="ncrm-contract-details__side-value--muted">—</span>
          </NcrmContractSideEditableRow>
        </div>
      </v-card>
    </template>
    <template #main>
      <v-card variant="flat" class="ncrm-contract-details__main-card">
        <v-tabs v-model="activeTab" density="comfortable" class="ncrm-contract-details__tabs-bar">
          <v-tab value="overview">{{ $t("ncrm.contracts.details.tabs.overview") }}</v-tab>
          <v-tab value="onboarding">{{ $t("ncrm.contracts.details.tabs.onboarding") }}</v-tab>
          <v-tab value="billing">{{ $t("ncrm.contracts.details.tabs.billing") }}</v-tab>
          <v-tab value="mailjet">{{ $t("ncrm.integrations.mailjet.activity.tab") }}</v-tab>
          <v-tab value="activity">{{ $t("ncrm.contracts.details.tabs.activity") }}</v-tab>
          <v-tab value="files">{{ $t("ncrm.contracts.details.tabs.files") }}</v-tab>
          <v-tab value="history">{{ $t("ncrm.contracts.details.tabs.history") }}</v-tab>
        </v-tabs>
        <v-alert
          v-if="showFulfillmentReadyAlert"
          type="success"
          density="comfortable"
          class="ma-4 mb-0"
        >
          <div class="d-flex align-center justify-space-between flex-wrap ga-3">
            <div>
              <div class="text-body-2 ncrm-text-strong">
                {{ $t("ncrm.contracts.details.fulfillment.title") }}
              </div>
              <div class="text-body-2 text-medium-emphasis">
                {{ $t("ncrm.contracts.details.fulfillment.hint") }}
              </div>
            </div>
            <v-btn color="success" variant="elevated" size="small" @click="openFulfillmentDialog">
              <v-icon start>mdi-check-decagram-outline</v-icon>
              {{ $t("ncrm.contracts.details.fulfillment.action") }}
            </v-btn>
          </div>
        </v-alert>
        <v-window v-model="activeTab" class="ncrm-contract-details__window" :touch="false">
          <v-window-item value="overview">
            <v-card-text>
              <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-3">
                <div class="text-subtitle-2 ncrm-text-strong">
                  {{ $t("ncrm.contracts.details.overview.itemsTitle") }}
                </div>
                <NcrmContractAddProductAction
                  :contract="contract" :contract-id="normalizedContractId" :contract-items="contractItems"
                  :deal-name-fallback="recordTitle" :loading="loading" :select-menu-props="selectMenuProps"
                  @success="handleAddProductSuccess" @notify="handlePaymentDialogNotification"
                />
              </div>
              <v-alert v-if="!contractItems.length" type="info" density="comfortable">
                {{ $t("ncrm.contracts.details.overview.emptyItems") }}
              </v-alert>
              <div v-else class="ncrm-contract-details__overview-items">
                <NcrmContractItemFieldsCard
                  v-for="item in contractItems"
                  :key="`contract-overview-item-${item.id}`"
                  :contract-id="normalizedContractId"
                  :contract-item="item"
                  preview-values
                  merge-sections
                  :show-field-meta="false"
                  @updated="loadContract"
                  @notify="handleItemFieldsNotification"
                />
              </div>
            </v-card-text>
          </v-window-item>
          <v-window-item value="onboarding">
            <v-card-text>
              <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
                <div class="text-subtitle-2 ncrm-text-strong">
                  {{ $t("ncrm.contracts.details.onboarding.title") }}
                </div>
                <v-btn size="small" variant="text" color="primary" @click="openOnboardingHome">
                  <v-icon start>mdi-rocket-launch-outline</v-icon>
                  {{ $t("ncrm.contracts.actions.openOnboardingModule") }}
                </v-btn>
              </div>
              <v-alert v-if="!onboardingItems.length" type="info" density="comfortable">
                {{ $t("ncrm.contracts.details.onboarding.empty") }}
              </v-alert>
              <v-list v-else lines="two" density="comfortable" class="pa-0">
                <v-list-item
                  v-for="entry in onboardingItems"
                  :key="`contract-onboarding-${entry.id}`"
                  class="ncrm-contract-details__list-item"
                  @click="openOnboarding(entry.id)"
                >
                  <v-list-item-title>{{ onboardingTitle(entry) }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ onboardingSubtitle(entry) }}
                  </v-list-item-subtitle>
                  <template #append>
                    <div class="d-flex align-center ga-2">
                      <v-chip size="x-small" :color="statusColor(entry.status)">
                        {{ statusLabel(entry.status) }}
                      </v-chip>
                      <v-icon size="18" class="text-medium-emphasis">mdi-chevron-right</v-icon>
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-window-item>
          <v-window-item value="billing">
            <v-card-text>
              <div class="ncrm-contract-details__section">
                <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-3">
                  <div class="text-subtitle-2 ncrm-text-strong">
                    {{ $t("ncrm.contracts.details.billing.plansTitle") }}
                  </div>
                  <v-btn size="small" variant="text" color="primary" @click="openCreateBillingPlanDialog">
                    <v-icon start>mdi-plus</v-icon>
                    {{ $t("ncrm.contracts.actions.addBillingPlan") }}
                  </v-btn>
                </div>
                <v-alert v-if="!billingPlanItems.length" type="info" density="comfortable">
                  {{ $t("ncrm.contracts.details.billing.emptyPlans") }}
                </v-alert>
                <v-data-table
                  v-else
                  :headers="billingPlanHeaders"
                  :items="billingPlanItems"
                  item-key="id"
                  class="elevation-0 ncrm-contract-details__table"
                  density="comfortable"
                  :items-per-page="100"
                  hide-default-footer
                >
                  <template #item.contract_item_id="{ item }">
                    <div class="d-flex align-center ga-2">
                      <div class="text-body-2 ncrm-text-strong">
                        {{ contractItemLabel(item.contract_item_id) }}
                      </div>
                      <v-tooltip v-if="item.notes" location="top">
                        <template #activator="{ props }">
                          <v-icon v-bind="props" size="16" class="text-medium-emphasis">mdi-note-text-outline</v-icon>
                        </template>
                        <span>{{ item.notes }}</span>
                      </v-tooltip>
                    </div>
                  </template>
                  <template #item.plan_type="{ item }">
                    {{ billingPlanTypeLabel(item.plan_type) }}
                  </template>
                  <template #item.status="{ item }">
                    <v-chip size="x-small" :color="statusColor(item.status)">
                      {{ billingPlanStatusLabel(item.status) }}
                    </v-chip>
                  </template>
                  <template #item.amount_per_cycle="{ item }">
                    {{ billingPlanDisplayAmount(item) }}
                  </template>
                  <template #item.next_due_at="{ item }">
                    {{ formatDate(item.next_due_at) }}
                  </template>
                  <template #item.actions="{ item }">
                    <div class="d-flex align-center justify-end ga-1">
                      <v-btn
                        v-if="billingPlanHasRecordablePayments(item)"
                        icon="mdi-cash-check"
                        size="x-small"
                        variant="text"
                        color="success"
                        @click.stop="openRecordPaymentDialog(item)"
                      />
                      <v-btn
                        icon="mdi-pencil-outline"
                        size="x-small"
                        variant="text"
                        @click.stop="openEditBillingPlanDialog(item)"
                      />
                      <v-btn
                        icon="mdi-delete-outline"
                        size="x-small"
                        variant="text"
                        color="error"
                        @click.stop="openDeleteEntityDialog('billingPlan', item)"
                      />
                    </div>
                  </template>
                </v-data-table>
              </div>
              <v-divider class="my-4" />
              <div class="ncrm-contract-details__section ncrm-contract-details__payments-section">
                <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-3">
                  <div class="text-subtitle-2 ncrm-text-strong">
                    {{ $t("ncrm.contracts.details.billing.paymentsTitle") }}
                  </div>
                  <v-btn size="small" variant="text" color="primary" @click="openRecordPaymentDialog">
                    <v-icon start>mdi-plus</v-icon>
                    {{ $t("ncrm.contracts.actions.addPayment") }}
                  </v-btn>
                </div>
                <v-alert v-if="!paymentItems.length" type="info" density="comfortable">
                  {{ $t("ncrm.contracts.details.billing.emptyPayments") }}
                </v-alert>
                <template v-else>
                  <div v-if="paymentStatusFilter" class="d-flex align-center ga-2 mb-2">
                    <v-chip
                      color="primary"
                      closable
                      @click:close="paymentStatusFilter = null"
                    >
                      {{ $t(`ncrm.contracts.details.payments.filter.${paymentStatusFilter}`) }}
                    </v-chip>
                  </div>
                </template>
                <v-data-table
                  v-if="paymentItems.length"
                  :headers="paymentHeaders"
                  :items="filteredPaymentItems"
                  item-key="id"
                  class="elevation-0 ncrm-contract-details__table"
                  density="comfortable"
                  :items-per-page="100"
                  hide-default-footer
                >
                  <template #item.billing_plan_id="{ item }">
                    <div class="text-body-2 ncrm-text-strong">
                      {{ billingPlanLabel(item.billing_plan_id) }}
                    </div>
                  </template>
                  <template #item.due_at="{ item }">
                    {{ formatDate(item.due_at) }}
                  </template>
                  <template #item.amount_due="{ item }">
                    {{ formatMoney(item.amount_due, contract?.currency_code) }}
                  </template>
                  <template #item.amount_paid="{ item }">
                    {{ formatMoney(item.amount_paid, contract?.currency_code) }}
                  </template>
                  <template #item.paid_at="{ item }">
                    {{ formatDate(item.paid_at) }}
                  </template>
                  <template #item.status="{ item }">
                    <v-chip size="x-small" :color="statusColor(item.status)">
                      {{ paymentStatusLabel(item.status) }}
                    </v-chip>
                  </template>
                  <template #item.actions="{ item }">
                    <div class="d-flex align-center justify-end ga-1">
                      <v-btn
                        icon="mdi-pencil-outline"
                        size="x-small"
                        variant="text"
                        @click.stop="openEditPaymentDialog(item)"
                      />
                      <v-btn
                        v-if="isPaymentDeletionAllowed(item)"
                        icon="mdi-delete-outline"
                        size="x-small"
                        variant="text"
                        color="error"
                        @click.stop="openDeleteEntityDialog('payment', item)"
                      />
                    </div>
                  </template>
                </v-data-table>
              </div>
            </v-card-text>
          </v-window-item>
          <v-window-item value="mailjet">
            <v-card-text>
              <NcrmMailjetActivityPanel
                v-if="activeTab === 'mailjet'"
                entity-type="contract"
                :entity-id="normalizedContractId"
                @notify="onMailjetNotify"
              />
            </v-card-text>
          </v-window-item>
          <v-window-item value="activity">
            <v-card-text>
              <NcrmActivityTimeline
                :key="`contract-activity-${normalizedContractId}-${activityRefreshKey}`"
                entity-type="contract"
                :entity-id="normalizedContractId"
                :title="$t('ncrm.contracts.details.activity.title')"
                :subtitle="$t('ncrm.contracts.details.activity.subtitle')"
                sort-mode="active_overdue_first"
              />
            </v-card-text>
          </v-window-item>
          <v-window-item value="files">
            <v-card-text>
              <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
                <div class="text-subtitle-2 ncrm-text-strong">
                  {{ $t("ncrm.contracts.details.files.title") }}
                </div>
                <v-btn size="small" variant="text" color="primary" @click="openCreateFileDialog">
                  <v-icon start>mdi-paperclip-plus</v-icon>
                  {{ $t("ncrm.contracts.actions.addFile") }}
                </v-btn>
              </div>
              <v-alert v-if="!fileItems.length" type="info" density="comfortable">
                {{ $t("ncrm.contracts.details.files.empty") }}
              </v-alert>
              <v-list v-else lines="two" density="comfortable" class="pa-0">
                <v-list-item
                  v-for="file in fileItems"
                  :key="`contract-file-${file.id}`"
                  class="ncrm-contract-details__list-item"
                >
                  <v-list-item-title>{{ valueOrDash(file.file_name) }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ fileCategoryLabel(file.file_category) }} · {{ formatBytes(file.file_size_bytes) }}
                  </v-list-item-subtitle>
                  <template #append>
                    <div class="d-flex align-center ga-1">
                      <v-btn
                        icon="mdi-open-in-new"
                        size="x-small"
                        variant="text"
                        @click.stop="openContractFile(file)"
                      />
                      <v-btn
                        icon="mdi-pencil-outline"
                        size="x-small"
                        variant="text"
                        @click.stop="openEditFileDialog(file)"
                      />
                      <v-btn
                        icon="mdi-delete-outline"
                        size="x-small"
                        variant="text"
                        color="error"
                        @click.stop="openDeleteEntityDialog('file', file)"
                      />
                      <div class="text-caption text-medium-emphasis ncrm-contract-details__file-date">
                        {{ formatDateTime(file.created_at) }}
                      </div>
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-window-item>
          <v-window-item value="history">
            <v-card-text>
              <v-alert v-if="!historyItems.length" type="info" density="comfortable">
                {{ $t("ncrm.contracts.details.history.empty") }}
              </v-alert>
              <v-list v-else lines="two" density="comfortable" class="pa-0">
                <v-list-item
                  v-for="entry in historyItems"
                  :key="`contract-history-${entry.id}`"
                  class="ncrm-contract-details__list-item"
                >
                  <v-list-item-title>
                    <v-chip size="x-small" color="info" class="mr-2">
                      {{ historyEventLabel(entry.event_type) }}
                    </v-chip>
                    {{ historyValueLabel(entry) }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ $t("ncrm.contracts.details.history.changedByAt", {
                      user: valueOrDash(entry.changed_by_name || entry.changed_by_user_id),
                      date: formatDateTime(entry.created_at),
                    }) }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-window-item>
        </v-window>
      </v-card>
    </template>
    <template #right>
      <v-card variant="flat" class="ncrm-contract-details__panel-card ncrm-contract-details__panel-card--payments pa-4">
        <div class="ncrm-contract-details__panel-title ncrm-contract-details__panel-title--payments mb-3">
          <v-icon size="15" class="ncrm-contract-details__panel-title-icon">mdi-cash-register</v-icon>
          {{ $t("ncrm.contracts.details.payments.title") }}
        </div>
        <div v-if="loading" class="ncrm-contract-details__skeleton-stack">
          <div v-for="n in 4" :key="n" class="ncrm-contract-details__skeleton-row">
            <v-skeleton-loader type="text" width="50%" />
            <v-skeleton-loader type="text" width="80%" />
          </div>
        </div>
        <div v-else class="ncrm-contract-details__side-stack">
          <div
            class="ncrm-contract-details__side-row ncrm-contract-details__side-row--clickable"
            role="button"
            tabindex="0"
            @click="drillDownToPayments(null)"
            @keydown.enter="drillDownToPayments(null)"
          >
            <div class="ncrm-contract-details__side-label">
              <v-icon size="14" class="ncrm-contract-details__row-icon">mdi-receipt-text-outline</v-icon>
              {{ $t("ncrm.contracts.details.payments.totalPayments") }}
            </div>
            <div class="ncrm-contract-details__side-value ncrm-contract-details__side-value--strong">
              {{ formatNumber(paymentSummary.total_payments) }}
              <v-icon size="14" class="ncrm-contract-details__side-drill-icon">mdi-arrow-right</v-icon>
            </div>
          </div>
          <div
            class="ncrm-contract-details__side-row ncrm-contract-details__side-row--clickable ncrm-contract-details__side-row--paid"
            role="button"
            tabindex="0"
            @click="drillDownToPayments('paid')"
            @keydown.enter="drillDownToPayments('paid')"
          >
            <div class="ncrm-contract-details__side-label">
              <v-icon size="14" class="ncrm-contract-details__row-icon ncrm-contract-details__row-icon--paid">mdi-check-circle-outline</v-icon>
              {{ $t("ncrm.contracts.details.payments.paidAmount") }}
            </div>
            <div class="ncrm-contract-details__side-value ncrm-contract-details__side-value--paid">
              {{ formatMoney(paymentSummary.paid_amount, contract?.currency_code) }}
              <v-icon size="14" class="ncrm-contract-details__side-drill-icon">mdi-arrow-right</v-icon>
            </div>
          </div>
          <div
            class="ncrm-contract-details__side-row ncrm-contract-details__side-row--clickable ncrm-contract-details__side-row--open"
            role="button"
            tabindex="0"
            @click="drillDownToPayments('open')"
            @keydown.enter="drillDownToPayments('open')"
          >
            <div class="ncrm-contract-details__side-label">
              <v-icon size="14" class="ncrm-contract-details__row-icon ncrm-contract-details__row-icon--open">mdi-circle-half-full</v-icon>
              {{ $t("ncrm.contracts.details.payments.openAmount") }}
            </div>
            <div class="ncrm-contract-details__side-value ncrm-contract-details__side-value--open">
              {{ formatMoney(paymentSummary.open_amount, contract?.currency_code) }}
              <v-icon size="14" class="ncrm-contract-details__side-drill-icon">mdi-arrow-right</v-icon>
            </div>
          </div>
          <!-- Restant + Health status — merged row -->
          <div
            class="ncrm-contract-details__health-status-row"
            :class="[
              `ncrm-contract-details__health-status-row--${contractHealth.level}`,
              Number(paymentSummary.overdue_amount || 0) > 0 ? 'ncrm-contract-details__health-status-row--clickable' : ''
            ]"
            :role="Number(paymentSummary.overdue_amount || 0) > 0 ? 'button' : undefined"
            :tabindex="Number(paymentSummary.overdue_amount || 0) > 0 ? 0 : undefined"
            @click="Number(paymentSummary.overdue_amount || 0) > 0 && drillDownToPayments('overdue')"
            @keydown.enter="Number(paymentSummary.overdue_amount || 0) > 0 && drillDownToPayments('overdue')"
          >
            <div class="ncrm-contract-details__health-status-left">
              <v-icon
                :color="contractHealth.color === 'default' ? undefined : contractHealth.color"
                size="15"
              >{{ contractHealth.icon }}</v-icon>
              <span
                class="ncrm-contract-details__health-status-label"
                :class="`ncrm-contract-details__health-status-label--${contractHealth.level}`"
              >{{ $t(`ncrm.contracts.details.health.${contractHealth.key}`) }}</span>
            </div>
            <div v-if="Number(paymentSummary.overdue_amount || 0) > 0" class="ncrm-contract-details__health-status-right">
              <v-chip color="error">
                {{ formatMoney(paymentSummary.overdue_amount, contract?.currency_code) }}
              </v-chip>
              <v-icon size="13" class="ncrm-contract-details__side-drill-icon">mdi-arrow-right</v-icon>
            </div>
          </div>
          <!-- Contract billing terms divider -->
          <div class="ncrm-contract-details__side-divider">
            <span class="ncrm-contract-details__side-divider-label">{{ $t("ncrm.contracts.details.payments.termsSection") }}</span>
          </div>
          <!-- Next due at -->
          <div v-if="paymentSummary.next_due_at" class="ncrm-contract-details__side-row">
            <div class="ncrm-contract-details__side-label">
              <v-icon size="14" class="ncrm-contract-details__row-icon">mdi-calendar-clock-outline</v-icon>
              {{ $t("ncrm.contracts.details.payments.nextDueAt") }}
            </div>
            <div class="ncrm-contract-details__side-value">{{ formatDate(paymentSummary.next_due_at) }}</div>
          </div>
          <!-- Auto-renew -->
          <div class="ncrm-contract-details__side-row">
            <div class="ncrm-contract-details__side-label">
              <v-icon size="14" class="ncrm-contract-details__row-icon">mdi-autorenew</v-icon>
              {{ $t("ncrm.contracts.details.context.autoRenew") }}
            </div>
            <div class="ncrm-contract-details__side-value">
              <v-chip
                v-if="Number(contract?.auto_renew) === 1 || contract?.auto_renew === true"
                size="x-small"
                color="primary"
              >
                <v-icon start size="12">mdi-autorenew</v-icon>
                {{ $t("ncrm.contracts.details.context.autoRenew") }}
              </v-chip>
              <span v-else class="ncrm-contract-details__side-value--muted">—</span>
            </div>
          </div>
          <!-- Payment terms (inline-editable) -->
          <NcrmContractSideEditableRow
            :label="$t('ncrm.contracts.details.context.paymentTerms')"
            icon="mdi-text-box-outline"
            :editing="inlineEdit.field === 'payment_terms_text'"
            @start="startInlineEdit('payment_terms_text')"
          >
            <template #editor>
              <v-text-field
                v-model="inlineEdit.value"
                density="compact"
                hide-details
                autofocus
                class="mt-1"
                :loading="inlineEdit.saving"
                @click.stop
                @keydown.enter="saveInlineEdit"
                @keydown.esc="cancelInlineEdit"
              >
                <template #append-inner>
                  <v-btn icon size="x-small" variant="text" color="success" :loading="inlineEdit.saving" @click.stop="saveInlineEdit">
                    <v-icon size="14">mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon size="x-small" variant="text" @click.stop="cancelInlineEdit">
                    <v-icon size="14">mdi-close</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </template>
            <span v-if="contract?.payment_terms_text">{{ contract.payment_terms_text }}</span>
            <button v-else class="ncrm-contract-details__placeholder-action" type="button" @click.stop="startInlineEdit('payment_terms_text')">
              <v-icon size="13" class="ncrm-contract-details__placeholder-action-icon">mdi-plus-circle-outline</v-icon>
              {{ $t("ncrm.contracts.details.context.addPaymentTerms") }}
            </button>
          </NcrmContractSideEditableRow>
        </div>
      </v-card>
    </template>
  </NcrmRecordLayout>
  <NcrmRecordEditDialog
    v-model="billingPlanDialog"
    :title="billingPlanDialogTitle"
    :subtitle="$t('ncrm.contracts.details.dialogs.billingPlan.subtitle')"
    :loading="billingPlanSaving"
    :persistent="billingPlanDialogPersistent"
    :close-disabled="billingPlanDialogPersistent"
    :cancel-disabled="billingPlanDialogPersistent"
    :save-disabled="!canSubmitBillingPlanForm"
    :save-label="billingPlanSaveLabel"
    @save="submitBillingPlanForm"
  >
    <v-row dense>
      <v-col cols="12">
        <v-select
          v-model="billingPlanForm.contract_item_id"
          :items="contractItemOptions"
          item-title="label"
          item-value="id"
          :menu-props="selectMenuProps"
          :disabled="billingPlanContractItemLocked"
          :label="$t('ncrm.contracts.details.dialogs.billingPlan.fields.contractItem')"
          @update:model-value="handleBillingPlanContractItemChange"
        />
      </v-col>
      <v-col v-if="!billingPlanSetupReady" cols="12">
        <v-alert type="info" density="comfortable">
          {{ $t("ncrm.contracts.details.dialogs.billingPlan.selectContractItemFirst") }}
        </v-alert>
      </v-col>
      <template v-else>
        <v-col v-if="billingSetupEnforced" cols="12">
          <v-alert type="warning" density="comfortable">
            {{
              $t("ncrm.contracts.details.dialogs.billingPlan.requiredQueue", {
                current: billingSetupCurrentStep,
                total: billingSetupTotalCount,
                item: billingSetupCurrentContractItemLabel,
              })
            }}
          </v-alert>
        </v-col>
        <v-col cols="12">
          <v-alert type="info" density="compact">
            {{
              $t("ncrm.contracts.details.dialogs.billingPlan.prefilledFromItem", {
                item: contractItemSelectionLabel(selectedBillingPlanContractItem),
              })
            }}
          </v-alert>
        </v-col>
        <v-col cols="12" sm="6">
          <v-select
            v-model="billingPlanForm.plan_type"
            :items="billingPlanTypeOptions"
            item-title="label"
            item-value="value"
            :menu-props="selectMenuProps"
            :label="$t('ncrm.contracts.details.dialogs.billingPlan.fields.planType')"
            @update:model-value="handleBillingPlanTypeChange"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-select
            v-model="billingPlanForm.status"
            :items="billingPlanStatusOptions"
            item-title="label"
            item-value="value"
            :menu-props="selectMenuProps"
            :label="$t('ncrm.contracts.details.dialogs.billingPlan.fields.status')"
          />
        </v-col>
        <v-col v-if="billingPlanShowAmountPerCycle" cols="12" sm="6">
          <v-text-field
            v-model="billingPlanForm.amount_per_cycle"
            type="number"
            step="0.01"
            min="0"
            :label="$t('ncrm.contracts.details.dialogs.billingPlan.fields.amountPerCycle')"
          />
        </v-col>
        <v-col v-if="billingPlanShowTotalPlannedAmount" cols="12" sm="6">
          <v-text-field
            v-model="billingPlanForm.total_planned_amount"
            type="number"
            step="0.01"
            min="0"
            :label="billingPlanTotalAmountLabel"
            @update:model-value="handleBillingPlanTotalAmountChange"
          />
        </v-col>
        <v-col v-if="billingPlanShowBillingFrequency" cols="12" sm="6">
          <v-select
            v-model="billingPlanForm.billing_frequency"
            :items="billingFrequencyOptions"
            item-title="label"
            item-value="value"
            clearable
            :menu-props="selectMenuProps"
            :label="$t('ncrm.contracts.details.dialogs.billingPlan.fields.billingFrequency')"
            @update:model-value="handleBillingPlanFrequencyChange"
          />
        </v-col>
        <v-col v-if="billingPlanShowBillingDayOfMonth" cols="12" sm="6">
          <v-text-field
            v-model="billingPlanForm.billing_day_of_month"
            type="number"
            min="1"
            max="31"
            step="1"
            :label="$t('ncrm.contracts.details.dialogs.billingPlan.fields.billingDayOfMonth')"
            @update:model-value="handleBillingPlanBillingDayChange"
          />
        </v-col>
        <v-col v-if="billingPlanShowStartDate" cols="12" sm="6">
          <v-text-field
            v-model="billingPlanForm.start_date"
            type="date"
            :label="$t('ncrm.contracts.details.dialogs.billingPlan.fields.startDate')"
            @update:model-value="handleBillingPlanStartDateChange"
          />
        </v-col>
        <v-col v-if="billingPlanShowEndDate" cols="12" sm="6">
          <v-text-field
            v-model="billingPlanForm.end_date"
            type="date"
            :label="$t('ncrm.contracts.details.dialogs.billingPlan.fields.endDate')"
            :hint="$t('ncrm.contracts.details.dialogs.billingPlan.endDateHint')"
            persistent-hint
          />
        </v-col>
        <v-col v-if="billingPlanShowNumberOfPayments" cols="12" sm="6">
          <v-text-field
            v-model="billingPlanForm.number_of_payments"
            type="number"
            min="1"
            step="1"
            :label="$t('ncrm.contracts.details.dialogs.billingPlan.fields.numberOfPayments')"
            @update:model-value="handleBillingPlanNumberOfPaymentsChange"
          />
        </v-col>
        <v-col v-if="billingPlanShowNextDueAt" cols="12" sm="6">
          <NcrmDateTimeField
            v-model="billingPlanForm.next_due_at"
            :label="billingPlanNextDueLabel"
            @update:model-value="handleBillingPlanNextDueAtChange"
          />
        </v-col>
        <v-col cols="12" :sm="billingPlanShowAutoRenew ? 6 : 12">
          <v-text-field
            v-model="billingPlanForm.currency_code"
            maxlength="8"
            :label="$t('ncrm.contracts.details.dialogs.billingPlan.fields.currencyCode')"
          />
        </v-col>
        <v-col v-if="billingPlanShowAutoRenew" cols="12" sm="6" class="d-flex align-center">
          <v-switch
            v-model="billingPlanForm.auto_renew"
            color="primary"
            hide-details
            :label="$t('ncrm.contracts.details.dialogs.billingPlan.fields.autoRenew')"
          />
        </v-col>
        <v-col cols="12">
          <v-textarea
            v-model="billingPlanForm.notes"
            rows="3"
            :label="$t('ncrm.contracts.details.dialogs.billingPlan.fields.notes')"
          />
        </v-col>
      </template>
    </v-row>
  </NcrmRecordEditDialog>
  <NcrmContractPaymentDialog
    ref="paymentDialogRef"
    :contract-id="normalizedContractId"
    :payment-items="paymentItems"
    :billing-plan-items="billingPlanItems"
    :contract-currency-code="contract?.currency_code"
    :select-menu-props="selectMenuProps"
    :billing-plan-label="billingPlanLabel"
    :format-date="formatDate"
    :format-money="formatMoney"
    @saved="loadContract"
    @notify="handlePaymentDialogNotification"
  />
  <NcrmContractTerminalBillingDialog
    v-model="terminalBillingDialog"
    v-model:resolution="terminalBillingResolution"
    :target-status="terminalBillingTargetStatus"
    :impact="terminalBillingDialogImpact"
    :currency-code="contract?.currency_code"
    :loading="terminalBillingSaving || statusSaving"
    :status-label="statusLabel"
    :format-money="formatMoney"
    :format-number="formatNumber"
    @confirm="confirmTerminalBillingStatus"
  />
  <NcrmRecordEditDialog
    v-model="fileDialog"
    :title="fileDialogTitle"
    :subtitle="$t('ncrm.contracts.details.dialogs.file.subtitle')"
    :loading="fileSaving"
    :save-disabled="!canSubmitFileForm"
    :save-label="fileSaveLabel"
    @save="submitFileForm"
  >
    <v-row dense>
      <v-col cols="12">
        <v-file-input
          v-model="fileForm.file"
          :label="$t('ncrm.contracts.details.dialogs.file.fields.binaryFile')"
          show-size
          prepend-icon=""
          prepend-inner-icon="mdi-paperclip"
          :accept="acceptedFileTypes"
          @update:model-value="onFileSelectionChanged"
        />
      </v-col>
      <v-col cols="12" sm="8">
        <v-text-field
          v-model="fileForm.file_name"
          :label="$t('ncrm.contracts.details.dialogs.file.fields.fileName')"
        />
      </v-col>
      <v-col cols="12" sm="4">
        <v-select
          v-model="fileForm.file_category"
          :items="fileCategoryOptions"
          item-title="label"
          item-value="value"
          :menu-props="selectMenuProps"
          :label="$t('ncrm.contracts.details.dialogs.file.fields.fileCategory')"
        />
      </v-col>
      <v-col cols="12">
        <v-alert
          v-if="editingFileId && fileForm.current_stored_path"
          type="info"
          density="compact"
        >
          {{ $t("ncrm.contracts.details.dialogs.file.currentPath", { path: fileForm.current_stored_path }) }}
        </v-alert>
      </v-col>
    </v-row>
  </NcrmRecordEditDialog>
  <NcrmRecordDeleteDialog
    v-model="deleteDialog.model"
    v-model:confirmText="deleteDialog.confirmText"
    :title="deleteDialog.title"
    :hint="deleteDialog.hint"
    :confirm-label="$t('ncrm.contracts.details.deleteDialog.confirmLabel')"
    :action-text="$t('app.actions.delete')"
    :loading="deleteDialog.loading"
    @confirm="confirmDeleteEntity"
  />
  <v-dialog v-model="fulfillmentDialog" max-width="520">
    <v-card>
      <v-card-title class="text-h6 py-4">
        {{ $t("ncrm.contracts.details.fulfillment.dialogTitle") }}
      </v-card-title>
      <v-card-text class="pt-0">
        <div class="text-body-2 text-medium-emphasis mb-3">
          {{ $t("ncrm.contracts.details.fulfillment.dialogHint") }}
        </div>
        <v-alert type="info" density="compact">
          {{
            $t("ncrm.contracts.details.fulfillment.summary", {
              onboardings: formatNumber(fulfillmentState.completed_onboarding_count || 0),
              openAmount: formatMoney(fulfillmentState.open_amount, contract?.currency_code),
              overdueAmount: formatMoney(fulfillmentState.overdue_amount, contract?.currency_code),
              openTasks: formatNumber(fulfillmentState.open_contract_activity_count || 0),
            })
          }}
        </v-alert>
      </v-card-text>
      <v-card-actions class="px-6 pb-4">
        <v-spacer />
        <v-btn variant="text" :disabled="fulfillmentSaving" @click="fulfillmentDialog = false">
          {{ $t("app.actions.cancel") }}
        </v-btn>
        <v-btn color="success" variant="elevated" :loading="fulfillmentSaving" @click="markContractAsFulfilled">
          {{ $t("ncrm.contracts.details.fulfillment.action") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3200">
    {{ snackbar.message }}
  </v-snackbar>
</template>
<script>
import axios from "axios";
import NcrmRecordLayout from "@/components/ncrm/shared/NcrmRecordLayout.vue";
import NcrmRecordHeader from "@/components/ncrm/shared/NcrmRecordHeader.vue";
import NcrmActivityTimeline from "@/components/ncrm/shared/NcrmActivityTimeline.vue";
import NcrmMailjetActivityPanel from "@/components/ncrm/integrations/mailjet/NcrmMailjetActivityPanel.vue";
import NcrmDateTimeField from "@/components/ncrm/shared/NcrmDateTimeField.vue";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import NcrmRecordDeleteDialog from "@/components/ncrm/shared/NcrmRecordDeleteDialog.vue";
import NcrmContractPaymentDialog from "@/components/ncrm/contracts/NcrmContractPaymentDialog.vue";
import NcrmContractItemFieldsCard from "@/components/ncrm/contracts/NcrmContractItemFieldsCard.vue";
import NcrmContractAddProductAction from "@/components/ncrm/contracts/NcrmContractAddProductAction.vue";
import NcrmContractEmailAction from "@/components/ncrm/contracts/NcrmContractEmailAction.vue";
import NcrmContractTerminalBillingDialog from "@/components/ncrm/contracts/NcrmContractTerminalBillingDialog.vue";
import NcrmContractSideEditableRow from "@/components/ncrm/contracts/NcrmContractSideEditableRow.vue";
import { openContractFileDownload } from "./contractFileDownload.mjs";
import { mapContractFileUpload } from "./contractFileUpload.mjs";
import {
  createTerminalBillingState,
  terminalBillingComputed,
  terminalBillingMethods,
} from "./NcrmContractTerminalBilling.logic.mjs";
import {
  addDaysToDateParts,
  addMonthsToDateParts,
  BILLING_FREQUENCIES,
  BILLING_PLAN_STATUSES,
  BILLING_PLAN_TYPES,
  buildLocalDate,
  calculatePaymentRemainingAmount,
  compareDateParts,
  CONTRACT_DETAILS_TABS,
  CONTRACT_STATUSES,
  daysInMonth,
  extractTimePartFromDateTimeInputValue,
  formatDateParts,
  formatDisplayDateParts,
  formatDisplayDateTimeParts,
  fromDateInputValue,
  fromDateTimeInputValue,
  isFilledValue,
  normalizeBillingFrequencyValue,
  normalizeBillingPlanTypeValue,
  normalizeEnumValue,
  normalizeIntegerInput,
  normalizeMoneyInput,
  parseDateLikeParts,
  parseDateParts,
  parseDateTimeLikeParts,
  parseNumeric,
  isPaymentCollectionRecordable,
  resolveNextDueDateTimeInputValue,
  toDateInputValue,
  toDateTimeInputValue,
  toMoneyInputValue,
} from "./contractDetailsShared.mjs";
import { formatDisplayAmount, formatDisplayNumber } from "@/Utile/displayFormat.mjs";
export default {
  name: "NcrmContractDetails",
  components: {
    NcrmRecordLayout,
    NcrmRecordHeader,
    NcrmActivityTimeline,
    NcrmDateTimeField,
    NcrmRecordEditDialog,
    NcrmRecordDeleteDialog,
    NcrmContractPaymentDialog,
    NcrmContractItemFieldsCard,
    NcrmContractAddProductAction,
    NcrmContractEmailAction,
    NcrmContractTerminalBillingDialog,
    NcrmContractSideEditableRow,
    NcrmMailjetActivityPanel,
  },
  props: {
    contractId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      activeTab: "overview",
      activityRefreshKey: 0,
      paymentStatusFilter: null,
      inlineEdit: { field: null, value: "", saving: false },
      ownerOptions: [],
      ownerLoading: false,
      ownerRequestSeq: 0,
      personOptions: [],
      personLoading: false,
      personRequestSeq: 0,
      personSearchDebounceId: null,
      contract: null,
      snackbar: { show: false, color: "info", message: "" },
      billingSetupActive: false,
      billingSetupInitialItemIds: [],
      billingSetupCurrentItemId: null,
      billingPlanDialog: false,
      billingPlanSaving: false,
      editingBillingPlanId: null,
      billingPlanForm: this.createEmptyBillingPlanForm(),
      fulfillmentDialog: false,
      fulfillmentSaving: false,
      statusMenu: false,
      statusSaving: false,
      ...createTerminalBillingState(),
      fileDialog: false,
      fileSaving: false,
      editingFileId: null,
      fileForm: this.createEmptyFileForm(),
      deleteDialog: {
        model: false,
        targetType: "",
        targetId: null,
        confirmText: "",
        loading: false,
        title: "",
        hint: "",
      },
      acceptedFileTypes: ".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.png,.jpg,.jpeg,.webp,.gif,.zip",
    };
  },
  computed: {
    normalizedContractId() {
      const parsed = Number(this.contractId || this.$route?.params?.contractId || 0);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      return Math.trunc(parsed);
    },
    breadcrumbItems() {
      return [
        {
          title: this.$t("ncrm.contracts.home.title"),
          to: { name: "NcrmContractsHome" },
          disabled: false,
        },
        {
          title: this.contractBreadcrumbLabel,
          disabled: true,
        },
      ];
    },
    contractBreadcrumbLabel() {
      const number = String(this.contract?.contract_number || "").trim();
      if (number) return number;
      if (this.normalizedContractId) return `#${this.normalizedContractId}`;
      return this.$t("ncrm.contracts.details.titleFallback");
    },
    contractHealth() {
      const status = this.normalizedContractStatus;
      if (status === "canceled") return { level: "neutral", color: "default", icon: "mdi-cancel", key: "canceled" };
      if (status === "fulfilled") return { level: "good", color: "success", icon: "mdi-check-circle-outline", key: "fulfilled" };
      const overdue = Number(this.paymentSummary?.overdue_amount || 0);
      if (overdue > 0) return { level: "critical", color: "error", icon: "mdi-alert-circle-outline", key: "overdue" };
      const nextDueDate = buildLocalDate(parseDateTimeLikeParts(this.paymentSummary?.next_due_at));
      if (nextDueDate) {
        const daysUntilDue = Math.ceil((nextDueDate - new Date()) / 86400000);
        if (daysUntilDue <= 7 && daysUntilDue >= 0) {
          return { level: "warning", color: "warning", icon: "mdi-clock-alert-outline", key: "dueSoon" };
        }
      }
      if (status === "active") return { level: "good", color: "success", icon: "mdi-check-circle-outline", key: "active" };
      return { level: "neutral", color: "default", icon: "mdi-circle-outline", key: "unknown" };
    },
    contractLifecycle() {
      const startRaw = this.contract?.start_date || this.contract?.contract_date;
      const endRaw = this.contract?.end_date;
      if (!startRaw) return null;
      const start = new Date(startRaw);
      const now = new Date();
      const end = endRaw ? new Date(endRaw) : null;
      const total = end ? end - start : null;
      const elapsed = now - start;
      const progressPct = total ? Math.min(100, Math.max(0, Math.round((elapsed / total) * 100))) : null;
      const isExpired = end && now > end;
      return { start, end, now, progressPct, isExpired, hasEnd: Boolean(end) };
    },
    recordTitle() {
      const company = String(this.contract?.company_name || "").trim();
      if (company) return company;
      return this.$t("ncrm.contracts.details.titleFallback");
    },
    recordSubtitle() {
      const number = String(this.contract?.contract_number || "").trim();
      const person = String(this.contract?.person_name || "").trim();
      if (number && person) return `${number} · ${person}`;
      return number || person || this.$t("ncrm.contracts.details.subtitleFallback");
    },
    normalizedContractStatus() {
      return String(this.contract?.status || "").trim().toLowerCase() || null;
    },
    canSelectFulfilledStatus() {
      return this.normalizedContractStatus === "fulfilled" || this.canMarkFulfilled;
    },
    contractStatusOptions() {
      return CONTRACT_STATUSES.map((value) => {
        const isFulfilled = value === "fulfilled";
        return {
          value,
          label: this.statusLabel(value),
          color: this.statusColor(value),
          disabled: isFulfilled && !this.canSelectFulfilledStatus,
          hint: isFulfilled && !this.canSelectFulfilledStatus
            ? this.$t("ncrm.contracts.details.header.fulfilledRequiresClosure")
            : "",
        };
      });
    },
    contractItems() {
      return Array.isArray(this.contract?.items) ? this.contract.items : [];
    },
    onboardingItems() {
      return Array.isArray(this.contract?.onboardings) ? this.contract.onboardings : [];
    },
    billingPlanItems() {
      return Array.isArray(this.contract?.billing_plans) ? this.contract.billing_plans : [];
    },
    paymentItems() {
      return Array.isArray(this.contract?.payments) ? this.contract.payments : [];
    },
    historyItems() {
      return Array.isArray(this.contract?.history) ? this.contract.history : [];
    },
    fileItems() {
      return Array.isArray(this.contract?.files) ? this.contract.files : [];
    },
    paymentSummary() {
      return this.contract?.payment_summary || {
        total_payments: 0,
        paid_amount: 0,
        open_amount: 0,
        overdue_amount: 0,
        next_due_at: null,
      };
    },
    ...terminalBillingComputed,
    fulfillmentState() {
      return this.contract?.fulfillment_state || {
        can_mark_fulfilled: false,
        is_already_fulfilled: false,
        is_one_time_contract: false,
        open_contract_activity_count: 0,
        billing_plan_count: 0,
        recurring_billing_plan_count: 0,
        onboarding_count: 0,
        completed_onboarding_count: 0,
        open_amount: 0,
        overdue_amount: 0,
        reasons: [],
      };
    },
    dealRoute() {
      const dealId = parseNumeric(this.contract?.deal_id, 0);
      if (!dealId) return null;
      return {
        name: "NcrmDealDetails",
        params: { id: String(dealId) },
      };
    },
    companyRoute() {
      const companyId = parseNumeric(this.contract?.ncrm_company_id, 0);
      if (!companyId) return null;
      return {
        name: "NcrmCompanyDetails",
        params: { id: String(companyId) },
      };
    },
    personRoute() {
      const personId = parseNumeric(this.contract?.ncrm_person_id, 0);
      if (!personId) return null;
      return {
        name: "NcrmPersonDetails",
        params: { id: String(personId) },
      };
    },
    hasOnboardingEntries() {
      return this.onboardingItems.length > 0;
    },
    canMarkFulfilled() {
      return this.fulfillmentState.can_mark_fulfilled === true;
    },
    showFulfillmentReadyAlert() {
      return this.canMarkFulfilled && !this.fulfillmentSaving;
    },
    hasDealLink() {
      return Boolean(this.dealRoute);
    },
    itemHeaders() {
      return [
        { title: this.$t("ncrm.contracts.details.overview.columns.product"), key: "product_name_snapshot" },
        { title: this.$t("ncrm.contracts.details.overview.columns.quantity"), key: "quantity" },
        { title: this.$t("ncrm.contracts.details.overview.columns.unitPrice"), key: "unit_price" },
        { title: this.$t("ncrm.contracts.details.overview.columns.total"), key: "total_amount" },
        { title: this.$t("ncrm.contracts.details.overview.columns.billing"), key: "billing_frequency" },
      ];
    },
    billingPlanHeaders() {
      return [
        { title: this.$t("ncrm.contracts.details.billing.columns.contractItem"), key: "contract_item_id" },
        { title: this.$t("ncrm.contracts.details.billing.columns.planType"), key: "plan_type" },
        { title: this.$t("ncrm.contracts.details.billing.columns.status"), key: "status" },
        { title: this.$t("ncrm.contracts.details.billing.columns.amountPerCycle"), key: "amount_per_cycle" },
        { title: this.$t("ncrm.contracts.details.billing.columns.nextDueAt"), key: "next_due_at" },
        { title: this.$t("ncrm.contracts.details.billing.columns.actions"), key: "actions", sortable: false, align: "end" },
      ];
    },
    paymentHeaders() {
      return [
        { title: this.$t("ncrm.contracts.details.billing.columns.billingPlan"), key: "billing_plan_id" },
        { title: this.$t("ncrm.contracts.details.billing.columns.dueAt"), key: "due_at" },
        { title: this.$t("ncrm.contracts.details.billing.columns.amountDue"), key: "amount_due" },
        { title: this.$t("ncrm.contracts.details.billing.columns.amountPaid"), key: "amount_paid" },
        { title: this.$t("ncrm.contracts.details.billing.columns.paidAt"), key: "paid_at" },
        { title: this.$t("ncrm.contracts.details.billing.columns.status"), key: "status" },
        { title: this.$t("ncrm.contracts.details.billing.columns.actions"), key: "actions", sortable: false, align: "end" },
      ];
    },
    contractItemOptions() {
      return this.contractItems.map((item) => ({
        id: parseNumeric(item?.id, 0),
        label: this.contractItemSelectionLabel(item),
      }));
    },
    billingSetupPendingItemIds() {
      const billingPlanItemIds = new Set(
        this.billingPlanItems
          .map((item) => parseNumeric(item?.contract_item_id, 0))
          .filter((value) => value > 0)
      );
      return this.contractItems
        .map((item) => parseNumeric(item?.id, 0))
        .filter((value) => value > 0 && !billingPlanItemIds.has(value));
    },
    billingSetupQueueItemIds() {
      const pendingSet = new Set(this.billingSetupPendingItemIds);
      const remainingInitial = (this.billingSetupInitialItemIds || []).filter((id) => pendingSet.has(id));
      return remainingInitial.length ? remainingInitial : this.billingSetupPendingItemIds;
    },
    billingSetupPendingCount() {
      return this.billingSetupQueueItemIds.length;
    },
    billingSetupTotalCount() {
      return this.billingSetupInitialItemIds.length || this.billingSetupPendingCount;
    },
    billingSetupCurrentStep() {
      if (!this.billingSetupPendingCount) return 0;
      const completedCount = Math.max(0, this.billingSetupTotalCount - this.billingSetupPendingCount);
      return completedCount + 1;
    },
    billingSetupCurrentContractItemId() {
      return parseNumeric(this.billingSetupCurrentItemId, 0) || this.billingSetupQueueItemIds[0] || null;
    },
    billingSetupCurrentContractItemLabel() {
      if (!this.billingSetupCurrentContractItemId) return "-";
      return this.contractItemLabel(this.billingSetupCurrentContractItemId);
    },
    billingSetupEnforced() {
      return Boolean(this.billingSetupActive && this.billingSetupPendingCount > 0);
    },
    billingPlanDialogPersistent() {
      return Boolean(this.billingSetupEnforced && !this.editingBillingPlanId);
    },
    billingPlanContractItemLocked() {
      return this.billingPlanDialogPersistent;
    },
    selectedBillingPlanContractItem() {
      return this.resolveBillingPlanContractItem();
    },
    billingPlanSetupReady() {
      return Boolean(this.selectedBillingPlanContractItem);
    },
    billingPlanTypeValue() {
      return normalizeBillingPlanTypeValue(this.billingPlanForm.plan_type, "one_time");
    },
    billingPlanFrequencyValue() {
      return normalizeBillingFrequencyValue(this.billingPlanForm.billing_frequency, "");
    },
    billingPlanShowAmountPerCycle() {
      return this.billingPlanSetupReady && !this.isBillingPlanTypeOneTime() && !this.isBillingPlanTypeMilestone();
    },
    billingPlanShowTotalPlannedAmount() {
      return this.billingPlanSetupReady && (
        this.isBillingPlanTypeOneTime()
        || this.isBillingPlanTypeInstallment()
        || this.isBillingPlanTypeMilestone()
      );
    },
    billingPlanShowBillingFrequency() {
      return this.billingPlanSetupReady && !this.isBillingPlanTypeOneTime() && !this.isBillingPlanTypeMilestone();
    },
    billingPlanShowBillingDayOfMonth() {
      return this.billingPlanSetupReady && this.isCalendarBillingFrequency();
    },
    billingPlanShowStartDate() {
      return this.billingPlanSetupReady && !this.isBillingPlanTypeOneTime() && !this.isBillingPlanTypeMilestone();
    },
    billingPlanShowEndDate() {
      return this.billingPlanSetupReady && this.isBillingPlanTypeRecurringLike();
    },
    billingPlanShowNumberOfPayments() {
      return this.billingPlanSetupReady && this.isBillingPlanTypeInstallment();
    },
    billingPlanShowNextDueAt() {
      return this.billingPlanSetupReady;
    },
    billingPlanShowAutoRenew() {
      return this.billingPlanSetupReady && this.isBillingPlanTypeRecurringLike();
    },
    billingPlanTotalAmountLabel() {
      if (this.isBillingPlanTypeOneTime() || this.isBillingPlanTypeMilestone()) {
        return this.$t("ncrm.contracts.details.dialogs.billingPlan.fields.plannedAmount");
      }
      return this.$t("ncrm.contracts.details.dialogs.billingPlan.fields.totalPlannedAmount");
    },
    billingPlanNextDueLabel() {
      if (this.isBillingPlanTypeOneTime() || this.isBillingPlanTypeMilestone()) {
        return this.$t("ncrm.contracts.details.dialogs.billingPlan.fields.dueAt");
      }
      return this.$t("ncrm.contracts.details.dialogs.billingPlan.fields.nextDueAt");
    },
    recordablePaymentItems() {
      return this.paymentItems.filter((item) => this.isRecordablePayment(item));
    },
    filteredPaymentItems() {
      if (!this.paymentStatusFilter) return this.paymentItems;
      const filter = this.paymentStatusFilter;
      return this.paymentItems.filter((item) => {
        const status = String(item?.status || "").toLowerCase();
        if (filter === "paid") return status === "paid";
        if (filter === "open") return status === "pending" || status === "partial" || status === "overdue";
        if (filter === "overdue") return status === "overdue";
        return true;
      });
    },
    billingPlanTypeOptions() {
      return BILLING_PLAN_TYPES.map((value) => ({
        value,
        label: this.billingPlanTypeLabel(value),
      }));
    },
    billingPlanStatusOptions() {
      return BILLING_PLAN_STATUSES.map((value) => ({
        value,
        label: this.billingPlanStatusLabel(value),
      }));
    },
    billingFrequencyOptions() {
      return BILLING_FREQUENCIES.map((value) => ({
        value,
        label: this.billingFrequencyLabel(value),
      }));
    },
    fileCategoryOptions() {
      const values = ["contract_document", "annex", "invoice", "payment_proof", "onboarding_asset", "other"];
      return values.map((value) => ({
        value,
        label: this.fileCategoryLabel(value),
      }));
    },
    billingPlanDialogTitle() {
      if (this.editingBillingPlanId) return this.$t("ncrm.contracts.details.dialogs.billingPlan.editTitle");
      return this.$t("ncrm.contracts.details.dialogs.billingPlan.createTitle");
    },
    billingPlanSaveLabel() {
      if (this.editingBillingPlanId) return this.$t("app.actions.save");
      return this.$t("app.actions.add");
    },
    fileDialogTitle() {
      if (this.editingFileId) return this.$t("ncrm.contracts.details.dialogs.file.editTitle");
      return this.$t("ncrm.contracts.details.dialogs.file.createTitle");
    },
    fileSaveLabel() {
      if (this.editingFileId) return this.$t("app.actions.save");
      return this.$t("app.actions.add");
    },
    selectMenuProps() {
      return {
        maxHeight: 320,
        contentClass: "ncrm-contract-details-select-menu",
        zIndex: 2710,
      };
    },
    canSubmitBillingPlanForm() {
      return parseNumeric(this.billingPlanForm.contract_item_id, 0) > 0 && !this.billingPlanSaving;
    },
    canSubmitFileForm() {
      const fileName = String(this.fileForm.file_name || "").trim();
      if (!fileName) return false;
      if (this.editingFileId) return !this.fileSaving;
      return Boolean(this.fileForm.file) && !this.fileSaving;
    },
  },
  watch: {
    "$route.params.contractId"(nextValue, previousValue) {
      if (String(nextValue || "") === String(previousValue || "")) return;
      this.loadContract();
    },
    "$route.query.tab": {
      immediate: true,
      handler() {
        this.applyActiveTabFromRoute();
      },
    },
    "$route.query.billingSetup": {
      immediate: true,
      handler() {
        if (this.isBillingSetupRouteRequested()) {
          this.billingSetupActive = true;
        } else if (!this.billingSetupPendingCount) {
          this.billingSetupActive = false;
          this.billingSetupInitialItemIds = [];
          this.billingSetupCurrentItemId = null;
        }
      },
    },
    activeTab(value) {
      this.syncRouteTab(value);
    },
  },
  mounted() {
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", this.handleBeforeUnload);
    }
    this.loadContract();
  },
  beforeRouteLeave(to, from, next) {
    if (!this.confirmAbandonBillingSetup()) {
      next(false);
      return;
    }
    next();
  },
  beforeUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("beforeunload", this.handleBeforeUnload);
    }
    this.clearPersonSearchDebounce();
  },
  methods: {
    onMailjetNotify(payload) {
      if (!payload) return;
      if (typeof this.showSnackbar === "function") {
        this.showSnackbar(payload.message, payload.color || "info");
      }
    },
    handleContractEmailSent() {
      this.activityRefreshKey += 1;
    },
    createEmptyBillingPlanForm() {
      return {
        contract_item_id: null,
        plan_type: "one_time",
        status: "active",
        currency_code: String(this.contract?.currency_code || "MDL").toUpperCase(),
        amount_per_cycle: "",
        total_planned_amount: "",
        billing_frequency: "",
        billing_day_of_month: "",
        start_date: "",
        end_date: "",
        number_of_payments: "",
        auto_renew: false,
        next_due_at: "",
        notes: "",
      };
    },
    resolveBillingPlanContractItem(contractItemId = this.billingPlanForm?.contract_item_id) {
      const normalizedId = parseNumeric(contractItemId, 0);
      if (!normalizedId) return null;
      return this.contractItems.find((entry) => parseNumeric(entry?.id, 0) === normalizedId) || null;
    },
    isBillingPlanTypeOneTime(planType = this.billingPlanForm?.plan_type) {
      return normalizeBillingPlanTypeValue(planType, "one_time") === "one_time";
    },
    isBillingPlanTypeRecurringLike(planType = this.billingPlanForm?.plan_type) {
      const normalized = normalizeBillingPlanTypeValue(planType, "one_time");
      return normalized === "recurring" || normalized === "subscription";
    },
    isBillingPlanTypeInstallment(planType = this.billingPlanForm?.plan_type) {
      return normalizeBillingPlanTypeValue(planType, "one_time") === "installment";
    },
    isBillingPlanTypeMilestone(planType = this.billingPlanForm?.plan_type) {
      return normalizeBillingPlanTypeValue(planType, "one_time") === "milestone";
    },
    isCalendarBillingFrequency(frequency = this.billingPlanForm?.billing_frequency) {
      const normalized = normalizeBillingFrequencyValue(frequency, "");
      return normalized === "monthly" || normalized === "quarterly" || normalized === "yearly";
    },
    resolveContractItemDefaultPlanType(item = {}) {
      const billingType = String(item?.billing_type || "").trim().toLowerCase();
      if (billingType === "recurring") {
        const productType = String(item?.product_type_snapshot || "").trim().toLowerCase();
        return productType.includes("subscription") ? "subscription" : "recurring";
      }
      return "one_time";
    },
    resolveContractItemDefaultAmount(item = {}) {
      const totalAmount = normalizeMoneyInput(item?.total_amount);
      if (totalAmount !== null && totalAmount > 0) return totalAmount;
      const unitPrice = normalizeMoneyInput(item?.unit_price);
      if (unitPrice !== null && unitPrice >= 0) return unitPrice;
      return null;
    },
    resolveContractItemDefaultFrequency(item = {}, planType = this.billingPlanForm?.plan_type) {
      if (this.isBillingPlanTypeOneTime(planType)) return "one_time";
      if (this.isBillingPlanTypeMilestone(planType)) return "";
      const frequency = normalizeBillingFrequencyValue(item?.billing_frequency, "");
      if (frequency && frequency !== "one_time") return frequency;
      if (this.isBillingPlanTypeInstallment(planType)) return "monthly";
      return "";
    },
    resolveContractItemDefaultStartDate(item = {}) {
      return (
        toDateInputValue(item?.service_start_date)
        || toDateInputValue(this.contract?.start_date)
        || toDateInputValue(this.contract?.contract_date)
      );
    },
    resolveContractItemDefaultEndDate(item = {}) {
      return toDateInputValue(item?.service_end_date);
    },
    resolveContractItemDefaultCurrency(item = {}) {
      return String(item?.currency || this.contract?.currency_code || "MDL").trim().toUpperCase() || "MDL";
    },
    resolveContractItemDefaultAutoRenew(item = {}) {
      return Number(item?.auto_renew) === 1 || item?.auto_renew === true;
    },
    resolveBillingDayFromStartDate(startDate = this.billingPlanForm?.start_date) {
      const parts = parseDateParts(startDate);
      return parts ? String(parts.day) : "";
    },
    resolveBillingPlanNextDueDefault() {
      const selectedItem = this.resolveBillingPlanContractItem();
      const startDate = String(this.billingPlanForm.start_date || this.resolveContractItemDefaultStartDate(selectedItem)).trim();
      return resolveNextDueDateTimeInputValue({
        planType: this.billingPlanForm.plan_type,
        billingFrequency: this.billingPlanForm.billing_frequency,
        startDate,
        billingDayOfMonth: this.billingPlanForm.billing_day_of_month,
        currentNextDueAt: this.billingPlanForm.next_due_at,
      });
    },
    syncBillingPlanDerivedFields({ resetDefaults = false } = {}) {
      const selectedItem = this.resolveBillingPlanContractItem();
      if (!selectedItem) return;
      const defaultAmount = this.resolveContractItemDefaultAmount(selectedItem);
      const defaultAmountInput = toMoneyInputValue(defaultAmount);
      const defaultStartDate = this.resolveContractItemDefaultStartDate(selectedItem);
      const defaultEndDate = this.resolveContractItemDefaultEndDate(selectedItem);
      const defaultFrequency = this.resolveContractItemDefaultFrequency(selectedItem, this.billingPlanForm.plan_type);
      if (!isFilledValue(this.billingPlanForm.currency_code) || resetDefaults) {
        this.billingPlanForm.currency_code = this.resolveContractItemDefaultCurrency(selectedItem);
      }
      if ((!isFilledValue(this.billingPlanForm.start_date) || resetDefaults) && defaultStartDate) {
        this.billingPlanForm.start_date = defaultStartDate;
      }
      if (this.isBillingPlanTypeOneTime()) {
        this.billingPlanForm.billing_frequency = "one_time";
        this.billingPlanForm.billing_day_of_month = "";
        this.billingPlanForm.number_of_payments = "";
        this.billingPlanForm.amount_per_cycle = "";
        this.billingPlanForm.end_date = "";
        this.billingPlanForm.auto_renew = false;
        if ((!isFilledValue(this.billingPlanForm.total_planned_amount) || resetDefaults) && defaultAmountInput) {
          this.billingPlanForm.total_planned_amount = defaultAmountInput;
        }
      } else if (this.isBillingPlanTypeRecurringLike()) {
        if ((!isFilledValue(this.billingPlanForm.billing_frequency) || resetDefaults) && defaultFrequency) {
          this.billingPlanForm.billing_frequency = defaultFrequency;
        }
        if ((!isFilledValue(this.billingPlanForm.amount_per_cycle) || resetDefaults) && defaultAmountInput) {
          this.billingPlanForm.amount_per_cycle = defaultAmountInput;
        }
        if ((!isFilledValue(this.billingPlanForm.end_date) || resetDefaults) && defaultEndDate) {
          this.billingPlanForm.end_date = defaultEndDate;
        }
        if (resetDefaults) {
          this.billingPlanForm.total_planned_amount = "";
          this.billingPlanForm.number_of_payments = "";
        }
        if (this.isCalendarBillingFrequency()) {
          if ((!isFilledValue(this.billingPlanForm.billing_day_of_month) || resetDefaults) && this.billingPlanForm.start_date) {
            this.billingPlanForm.billing_day_of_month = this.resolveBillingDayFromStartDate(this.billingPlanForm.start_date);
          }
        } else {
          this.billingPlanForm.billing_day_of_month = "";
        }
        if (resetDefaults) {
          this.billingPlanForm.auto_renew = this.resolveContractItemDefaultAutoRenew(selectedItem);
        }
      } else if (this.isBillingPlanTypeInstallment()) {
        if ((!isFilledValue(this.billingPlanForm.billing_frequency) || resetDefaults) && defaultFrequency) {
          this.billingPlanForm.billing_frequency = defaultFrequency;
        }
        if ((!isFilledValue(this.billingPlanForm.total_planned_amount) || resetDefaults) && defaultAmountInput) {
          this.billingPlanForm.total_planned_amount = defaultAmountInput;
        }
        if (resetDefaults) {
          this.billingPlanForm.end_date = "";
          this.billingPlanForm.auto_renew = false;
        }
        if (this.isCalendarBillingFrequency()) {
          if ((!isFilledValue(this.billingPlanForm.billing_day_of_month) || resetDefaults) && this.billingPlanForm.start_date) {
            this.billingPlanForm.billing_day_of_month = this.resolveBillingDayFromStartDate(this.billingPlanForm.start_date);
          }
        } else {
          this.billingPlanForm.billing_day_of_month = "";
        }
        const numberOfPayments = normalizeIntegerInput(this.billingPlanForm.number_of_payments);
        const totalPlannedAmount = normalizeMoneyInput(this.billingPlanForm.total_planned_amount);
        if (numberOfPayments && totalPlannedAmount !== null) {
          this.billingPlanForm.amount_per_cycle = toMoneyInputValue(totalPlannedAmount / numberOfPayments);
        } else if (resetDefaults) {
          this.billingPlanForm.amount_per_cycle = "";
        }
      } else if (this.isBillingPlanTypeMilestone()) {
        this.billingPlanForm.billing_frequency = "";
        this.billingPlanForm.billing_day_of_month = "";
        this.billingPlanForm.number_of_payments = "";
        this.billingPlanForm.amount_per_cycle = "";
        this.billingPlanForm.end_date = "";
        this.billingPlanForm.auto_renew = false;
        if ((!isFilledValue(this.billingPlanForm.total_planned_amount) || resetDefaults) && defaultAmountInput) {
          this.billingPlanForm.total_planned_amount = defaultAmountInput;
        }
      }
      this.billingPlanForm.next_due_at = this.resolveBillingPlanNextDueDefault();
    },
    handleBillingPlanContractItemChange(value) {
      this.billingPlanForm.contract_item_id = parseNumeric(value, 0) || null;
      if (!this.billingPlanForm.contract_item_id) return;
      const selectedItem = this.resolveBillingPlanContractItem();
      this.billingPlanForm.plan_type = this.resolveContractItemDefaultPlanType(selectedItem);
      this.syncBillingPlanDerivedFields({ resetDefaults: true });
    },
    handleBillingPlanTypeChange(value) {
      this.billingPlanForm.plan_type = normalizeBillingPlanTypeValue(value, "one_time");
      this.syncBillingPlanDerivedFields({ resetDefaults: true });
    },
    handleBillingPlanFrequencyChange(value) {
      this.billingPlanForm.billing_frequency = normalizeBillingFrequencyValue(value, "");
      this.syncBillingPlanDerivedFields();
    },
    handleBillingPlanStartDateChange(value) {
      this.billingPlanForm.start_date = toDateInputValue(value);
      this.syncBillingPlanDerivedFields();
    },
    handleBillingPlanBillingDayChange(value) {
      this.billingPlanForm.billing_day_of_month = value;
      this.billingPlanForm.next_due_at = this.resolveBillingPlanNextDueDefault();
    },
    handleBillingPlanNextDueAtChange(value) {
      this.billingPlanForm.next_due_at = value;
      if (!this.isCalendarBillingFrequency(this.billingPlanForm.billing_frequency)) return;
      if (!this.isBillingPlanTypeRecurringLike() && !this.isBillingPlanTypeInstallment()) return;
      const nextDueParts = parseDateParts(value);
      if (!nextDueParts) return;
      this.billingPlanForm.billing_day_of_month = String(nextDueParts.day);
    },
    handleBillingPlanNumberOfPaymentsChange(value) {
      this.billingPlanForm.number_of_payments = value;
      this.syncBillingPlanDerivedFields();
    },
    handleBillingPlanTotalAmountChange(value) {
      this.billingPlanForm.total_planned_amount = value;
      if (this.isBillingPlanTypeInstallment()) {
        this.syncBillingPlanDerivedFields();
      }
    },
    paymentRemainingAmount(payment = null) {
      return calculatePaymentRemainingAmount(payment);
    },
    isRecordablePayment(payment = null) {
      return isPaymentCollectionRecordable(payment);
    },
    billingPlanHasRecordablePayments(billingPlan = null) {
      const billingPlanId = parseNumeric(billingPlan?.id ?? billingPlan?.billing_plan_id, 0);
      if (!billingPlanId) return false;
      return this.recordablePaymentItems.some((item) => parseNumeric(item?.billing_plan_id, 0) === billingPlanId);
    },
    recordablePaymentLabel(payment = null) {
      if (!payment) return "-";
      return [
        this.billingPlanLabel(payment?.billing_plan_id),
        this.formatDate(payment?.due_at),
        this.formatMoney(this.paymentRemainingAmount(payment), this.contract?.currency_code),
      ].join(" · ");
    },
    createEmptyFileForm() {
      return {
        file: null,
        file_name: "",
        file_category: "contract_document",
        current_stored_path: "",
      };
    },
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        color,
        message,
      };
    },
    resolveActiveTabFromRoute() {
      const routeTab = String(this.$route?.query?.tab || "").trim().toLowerCase();
      return CONTRACT_DETAILS_TABS.includes(routeTab) ? routeTab : "";
    },
    applyActiveTabFromRoute() {
      const routeTab = this.resolveActiveTabFromRoute();
      if (!routeTab || routeTab === this.activeTab) return;
      this.activeTab = routeTab;
    },
    syncRouteTab(tab) {
      const normalized = CONTRACT_DETAILS_TABS.includes(tab) ? tab : "overview";
      const current = String(this.$route?.query?.tab || "").trim().toLowerCase();
      if (current === normalized) return;
      this.$router
        .replace({
          query: {
            ...(this.$route?.query || {}),
            tab: normalized,
          },
        })
        .catch(() => {});
    },
    isBillingSetupRouteRequested() {
      return String(this.$route?.query?.billingSetup || "").trim() === "1";
    },
    resolveMissingBillingPlanContractItemIds() {
      const billingPlanItemIds = new Set(
        this.billingPlanItems
          .map((item) => parseNumeric(item?.contract_item_id, 0))
          .filter((value) => value > 0)
      );
      return this.contractItems
        .map((item) => parseNumeric(item?.id, 0))
        .filter((value) => value > 0 && !billingPlanItemIds.has(value));
    },
    async clearBillingSetupRouteQuery() {
      if (!Object.prototype.hasOwnProperty.call(this.$route?.query || {}, "billingSetup")) return;
      const nextQuery = { ...(this.$route?.query || {}) };
      delete nextQuery.billingSetup;
      await this.$router
        .replace({ query: nextQuery })
        .catch(() => {});
    },
    async completeBillingSetupFlow() {
      this.billingSetupActive = false;
      this.billingSetupInitialItemIds = [];
      this.billingSetupCurrentItemId = null;
      this.billingPlanDialog = false;
      await this.clearBillingSetupRouteQuery();
    },
    async ensureBillingSetupState() {
      if (this.isBillingSetupRouteRequested()) {
        this.billingSetupActive = true;
      }
      if (!this.contract) return;
      const pendingIds = this.resolveMissingBillingPlanContractItemIds();
      if (!this.billingSetupActive) {
        if (!pendingIds.length) {
          this.billingSetupInitialItemIds = [];
          this.billingSetupCurrentItemId = null;
        }
        return;
      }
      if (!this.billingSetupInitialItemIds.length) {
        this.billingSetupInitialItemIds = [...pendingIds];
      }
      const pendingSet = new Set(pendingIds);
      const queueIds = (this.billingSetupInitialItemIds || []).filter((id) => pendingSet.has(id));
      const effectiveQueueIds = queueIds.length ? queueIds : pendingIds;
      if (!effectiveQueueIds.length) {
        await this.completeBillingSetupFlow();
        return;
      }
      const nextItemId = effectiveQueueIds[0];
      this.billingSetupCurrentItemId = nextItemId;
      if (this.activeTab !== "billing") {
        this.activeTab = "billing";
      }
      const selectedContractItemId = parseNumeric(this.billingPlanForm?.contract_item_id, 0) || null;
      if (!this.billingPlanDialog || this.editingBillingPlanId || selectedContractItemId !== nextItemId) {
        this.openCreateBillingPlanDialog({ contractItemId: nextItemId });
      }
    },
    shouldWarnBeforeLeavingBillingSetup() {
      return Boolean(this.billingSetupEnforced);
    },
    confirmAbandonBillingSetup() {
      if (!this.shouldWarnBeforeLeavingBillingSetup() || typeof window === "undefined") return true;
      return window.confirm(this.$t("ncrm.contracts.details.dialogs.billingPlan.requiredLeaveConfirm"));
    },
    handleBeforeUnload(event) {
      if (!this.shouldWarnBeforeLeavingBillingSetup()) return;
      if (event) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    valueOrDash(value) {
      const normalized = String(value ?? "").trim();
      return normalized || "-";
    },
    formatNumber(value) {
      return formatDisplayNumber(parseNumeric(value, 0));
    },
    formatDate(value) {
      const parts = parseDateLikeParts(value);
      if (!parts) {
        const normalized = String(value || "").trim();
        return normalized || "-";
      }
      return formatDisplayDateParts(parts);
    },
    formatDateTime(value) {
      const parts = parseDateTimeLikeParts(value);
      if (!parts) {
        const normalized = String(value || "").trim();
        return normalized || "-";
      }
      return formatDisplayDateTimeParts(parts);
    },
    formatMoney(value, currency = "MDL") {
      if (value === undefined || value === null || String(value).trim() === "") {
        return "-";
      }
      const amount = parseNumeric(value, null);
      if (amount === null) return "-";
      return `${formatDisplayAmount(amount)} ${String(currency || "MDL").toUpperCase()}`;
    },
    formatBytes(value) {
      const size = parseNumeric(value, 0);
      if (!size) return "-";
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
      if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
      return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    },
    statusLabel(status) {
      const normalized = String(status || "").trim().toLowerCase();
      if (!normalized) return "-";
      const key = `ncrm.contracts.statuses.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    statusColor(status) {
      const normalized = String(status || "").trim().toLowerCase();
      if (normalized === "active" || normalized === "fulfilled" || normalized === "paid" || normalized === "completed") return "success";
      if (normalized === "draft" || normalized === "pending") return "info";
      if (normalized === "expired" || normalized === "overdue" || normalized === "paused" || normalized === "partial") return "warning";
      if (normalized === "terminated" || normalized === "canceled") return "error";
      return "primary";
    },
    statusToneClass(status) {
      return `is-${this.statusColor(status)}`;
    },
    billingFrequencyLabel(value) {
      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return "-";
      const key = `ncrm.contracts.billingFrequency.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    billingPlanTypeLabel(value) {
      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return "-";
      const key = `ncrm.contracts.planTypes.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    billingPlanStatusLabel(value) {
      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return "-";
      const key = `ncrm.contracts.billingPlanStatuses.${normalized}`;
      const translated = this.$t(key);
      if (translated !== key) return translated;
      return this.statusLabel(normalized);
    },
    paymentStatusLabel(value) {
      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return "-";
      const key = `ncrm.contracts.paymentStatuses.${normalized}`;
      const translated = this.$t(key);
      if (translated !== key) return translated;
      return this.statusLabel(normalized);
    },
    fileCategoryLabel(value) {
      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return "-";
      const key = `ncrm.contracts.fileCategories.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    historyEventLabel(value) {
      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return "-";
      const key = `ncrm.contracts.history.eventTypes.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    historyValueLabel(entry) {
      const fromValue = String(entry?.from_value || "").trim();
      const toValue = String(entry?.to_value || "").trim();
      if (!fromValue && !toValue) return this.$t("ncrm.contracts.details.history.noValueChange");
      return this.$t("ncrm.contracts.details.history.valueTransition", {
        from: fromValue || "-",
        to: toValue || "-",
      });
    },
    onboardingTitle(entry) {
      const templateName = String(entry?.template_name || "").trim();
      if (templateName) return templateName;
      const stageName = String(entry?.current_stage_name || "").trim();
      if (stageName) return stageName;
      return `#${parseNumeric(entry?.id, 0) || "-"}`;
    },
    onboardingSubtitle(entry) {
      const stage = String(entry?.current_stage_name || "").trim() || this.$t("ncrm.onboarding.board.stageFallback");
      const kickoff = this.formatDate(entry?.kickoff_at);
      return `${stage} · ${this.$t("ncrm.onboarding.details.context.kickoffAt")}: ${kickoff}`;
    },
    contractItemSelectionLabel(item) {
      const normalizedId = parseNumeric(item?.id, 0);
      const productName = String(item?.product_name_snapshot || item?.product_name || "").trim();
      if (productName) return productName;
      if (normalizedId) return `#${normalizedId}`;
      return "-";
    },
    contractItemLabel(contractItemId) {
      const normalizedId = parseNumeric(contractItemId, 0);
      if (!normalizedId) return "-";
      const item = this.contractItems.find((entry) => parseNumeric(entry?.id, 0) === normalizedId);
      if (!item) return `#${normalizedId}`;
      const productName = String(item.product_name_snapshot || item.product_name || "").trim();
      return productName || `#${normalizedId}`;
    },
    billingPlanDisplayAmount(plan = {}) {
      const normalizedPlanType = normalizeBillingPlanTypeValue(plan?.plan_type, "one_time");
      const amountValue =
        normalizedPlanType === "one_time" || normalizedPlanType === "milestone"
          ? plan?.total_planned_amount
          : plan?.amount_per_cycle;
      return this.formatMoney(amountValue, plan?.currency_code || this.contract?.currency_code);
    },
    billingPlanLabel(billingPlanId) {
      const normalizedId = parseNumeric(billingPlanId, 0);
      if (!normalizedId) return "-";
      const plan = this.billingPlanItems.find((entry) => parseNumeric(entry?.id, 0) === normalizedId);
      if (!plan) return `#${normalizedId}`;
      return `${this.contractItemLabel(plan.contract_item_id)} · ${this.billingPlanTypeLabel(plan.plan_type)}`;
    },
    paymentBillingPlan(payment = null) {
      const billingPlanId = parseNumeric(payment?.billing_plan_id, 0);
      if (!billingPlanId) return null;
      return this.billingPlanItems.find((entry) => parseNumeric(entry?.id, 0) === billingPlanId) || null;
    },
    isPaymentDeletionAllowed(payment = null) {
      const billingPlan = this.paymentBillingPlan(payment);
      if (!billingPlan) return true;
      const normalizedStatus = String(billingPlan?.status || "").trim().toLowerCase();
      return normalizedStatus !== "draft" && normalizedStatus !== "active";
    },
    paymentDeleteLabel(payment = null) {
      if (!payment) return this.$t("ncrm.contracts.details.deleteDialog.fallbacks.payment");
      const parts = [];
      const productName = String(payment?.product_name_snapshot || "").trim();
      if (productName) {
        parts.push(productName);
      } else {
        const contractItemId = parseNumeric(payment?.contract_item_id, 0);
        const contractItem = this.contractItems.find((entry) => parseNumeric(entry?.id, 0) === contractItemId) || null;
        const fallbackProductName = String(contractItem?.product_name_snapshot || contractItem?.product_name || "").trim();
        if (fallbackProductName) {
          parts.push(fallbackProductName);
        }
      }
      const dueDate = this.formatDate(payment?.due_at);
      if (dueDate && dueDate !== "-") {
        parts.push(dueDate);
      }
      const amountDue = this.formatMoney(payment?.amount_due, this.contract?.currency_code);
      if (amountDue && amountDue !== "-") {
        parts.push(amountDue);
      }
      return parts.length
        ? parts.join(" · ")
        : this.$t("ncrm.contracts.details.deleteDialog.fallbacks.payment");
    },
    goBack() {
      this.$router.push({ name: "NcrmContractsHome" });
    },
    openLinkedRoute(route) {
      if (!route) return;
      this.$router.push(route);
    },
    drillDownToPayments(filter) {
      this.paymentStatusFilter = filter || null;
      this.activeTab = "billing";
      this.$nextTick(() => {
        const el = document.querySelector(".ncrm-contract-details__payments-section");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    startInlineEdit(field) {
      const rawMap = {
        payment_terms_text: this.contract?.payment_terms_text || "",
        notes: this.contract?.notes || "",
        end_date: this.contract?.end_date ? String(this.contract.end_date).slice(0, 10) : "",
      };
      this.inlineEdit = { field, value: rawMap[field] ?? "", saving: false };
    },
    startOwnerEdit() {
      const currentOwnerId = Number(this.contract?.owner_user_id || 0) || null;
      this.inlineEdit = { field: "owner_user_id", value: currentOwnerId, saving: false };
      this.seedOwnerOptionFromContract();
      this.fetchContractOwners();
    },
    seedOwnerOptionFromContract() {
      const currentOwnerId = Number(this.contract?.owner_user_id || 0) || null;
      if (!currentOwnerId) return;
      if (this.ownerOptions.some((option) => option.id === currentOwnerId)) return;
      const label = String(this.contract?.owner_name || "").trim() || `#${currentOwnerId}`;
      this.ownerOptions = [{ id: currentOwnerId, label }, ...this.ownerOptions];
    },
    async fetchContractOwners() {
      const requestSeq = ++this.ownerRequestSeq;
      this.ownerLoading = true;
      try {
        const { data } = await axios.get("/ncrm/contracts/owners", {
          params: { limit: 100 },
        });
        if (requestSeq !== this.ownerRequestSeq) return;
        this.ownerOptions = (Array.isArray(data?.items) ? data.items : []).map((item) => ({
          id: Number(item?.id || 0),
          label: item?.display_name || item?.name || item?.email || `#${item?.id}`,
        })).filter((option) => option.id);
        this.seedOwnerOptionFromContract();
      } catch (error) {
        console.error("[ncrm-contract-details] load owners error", error);
      } finally {
        if (requestSeq === this.ownerRequestSeq) {
          this.ownerLoading = false;
        }
      }
    },
    startPersonEdit() {
      const currentPersonId = Number(this.contract?.ncrm_person_id || 0) || null;
      this.inlineEdit = { field: "ncrm_person_id", value: currentPersonId, saving: false };
      this.seedPersonOptionFromContract();
      this.fetchContractPeople();
    },
    seedPersonOptionFromContract() {
      const currentPersonId = Number(this.contract?.ncrm_person_id || 0) || null;
      if (!currentPersonId) return;
      if (this.personOptions.some((option) => option.id === currentPersonId)) return;
      const label = String(this.contract?.person_name || "").trim() || `#${currentPersonId}`;
      this.personOptions = [{ id: currentPersonId, label }, ...this.personOptions];
    },
    onPersonSearch(search) {
      this.clearPersonSearchDebounce();
      this.personSearchDebounceId = setTimeout(() => {
        this.fetchContractPeople(search);
      }, 300);
    },
    async fetchContractPeople(search = "") {
      const requestSeq = ++this.personRequestSeq;
      this.personLoading = true;
      try {
        const normalizedSearch = String(search || "").trim();
        const { data } = await axios.get("/ncrm/contracts/people", {
          params: { search: normalizedSearch || undefined, limit: 50 },
        });
        if (requestSeq !== this.personRequestSeq) return;
        this.personOptions = (Array.isArray(data?.items) ? data.items : []).map((item) => {
          const id = Number(item?.id || 0);
          const name = item?.display_name || `#${id}`;
          const detail = [item?.company_name, item?.email_primary].map((v) => String(v || "").trim()).filter(Boolean).join(" • ");
          return { id, label: detail ? `${name} (${detail})` : name };
        }).filter((option) => option.id);
        this.seedPersonOptionFromContract();
      } catch (error) {
        console.error("[ncrm-contract-details] load people error", error);
      } finally {
        if (requestSeq === this.personRequestSeq) {
          this.personLoading = false;
        }
      }
    },
    clearPersonSearchDebounce() {
      if (this.personSearchDebounceId) {
        clearTimeout(this.personSearchDebounceId);
        this.personSearchDebounceId = null;
      }
    },
    cancelInlineEdit() {
      this.clearPersonSearchDebounce();
      this.inlineEdit = { field: null, value: "", saving: false };
    },
    async saveInlineEdit() {
      if (!this.inlineEdit.field || !this.normalizedContractId) return;
      this.inlineEdit.saving = true;
      try {
        await axios.patch(`/ncrm/contracts/${this.normalizedContractId}`, {
          [this.inlineEdit.field]: this.inlineEdit.value || null,
        });
        await this.loadContract();
        this.cancelInlineEdit();
        this.showSnackbar(this.$t("ncrm.contracts.messages.fieldSaved"), "success");
      } catch (error) {
        console.error("[ncrm-contract-details] inline edit save failed", error);
        this.showSnackbar(this.$t("ncrm.contracts.messages.saveFailed"), "error");
      } finally {
        this.inlineEdit.saving = false;
      }
    },
    openDeal() {
      this.openLinkedRoute(this.dealRoute);
    },
    openCompany() {
      this.openLinkedRoute(this.companyRoute);
    },
    openPerson() {
      this.openLinkedRoute(this.personRoute);
    },
    openOnboarding(onboardingId) {
      const normalized = parseNumeric(onboardingId, 0);
      if (!normalized) return;
      this.$router.push({
        name: "NcrmOnboardingDetails",
        params: { onboardingId: String(normalized) },
      });
    },
    openOnboardingShortcut() {
      if (!this.hasOnboardingEntries) return;
      const firstOnboardingId = parseNumeric(this.onboardingItems[0]?.id, 0);
      if (this.onboardingItems.length === 1 && firstOnboardingId) {
        this.openOnboarding(firstOnboardingId);
        return;
      }
      this.activeTab = "onboarding";
      this.syncRouteTab("onboarding");
    },
    openOnboardingHome() {
      this.$router.push({ name: "NcrmOnboardingHome" });
    },
    async handleAddProductSuccess(payload = {}) {
      const createdItemIds = Array.isArray(payload?.contractItemIds) ? payload.contractItemIds : [];
      if (createdItemIds.length) {
        this.billingSetupActive = true;
        this.billingSetupInitialItemIds = createdItemIds;
        this.billingSetupCurrentItemId = createdItemIds[0] || null;
      }
      await this.loadContract();
      this.showSnackbar(this.$t("ncrm.contracts.messages.productAdded"), "success");
    },
    ...terminalBillingMethods,
    openFulfillmentDialog() {
      if (!this.canMarkFulfilled) return;
      this.fulfillmentDialog = true;
    },
    async markContractAsFulfilled() {
      if (!this.normalizedContractId || !this.canMarkFulfilled) return;
      this.fulfillmentSaving = true;
      try {
        const { data } = await axios.post(`/ncrm/contracts/${this.normalizedContractId}/mark-fulfilled`);
        this.contract = data?.contract || this.contract;
        this.fulfillmentDialog = false;
        this.statusMenu = false;
        this.showSnackbar(this.$t("ncrm.contracts.messages.fulfilledMarked"), "success");
        await this.ensureBillingSetupState();
      } catch (error) {
        console.error("[ncrm-contract-details] mark fulfilled failed", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contracts.messages.fulfilledMarkError"),
          "error"
        );
      } finally {
        this.fulfillmentSaving = false;
      }
    },
    openCreateBillingPlanDialog({ contractItemId = null } = {}) {
      this.editingBillingPlanId = null;
      this.billingPlanForm = this.createEmptyBillingPlanForm();
      const normalizedContractItemId = parseNumeric(contractItemId, 0) || null;
      if (normalizedContractItemId) {
        this.billingPlanForm.contract_item_id = normalizedContractItemId;
        this.handleBillingPlanContractItemChange(this.billingPlanForm.contract_item_id);
      } else if (this.contractItemOptions.length === 1) {
        this.billingPlanForm.contract_item_id = parseNumeric(this.contractItemOptions[0]?.id, 0) || null;
        this.handleBillingPlanContractItemChange(this.billingPlanForm.contract_item_id);
      }
      this.billingPlanDialog = true;
    },
    openEditBillingPlanDialog(plan) {
      if (this.billingSetupEnforced) return;
      this.editingBillingPlanId = parseNumeric(plan?.id, 0) || null;
      this.billingPlanForm = {
        contract_item_id: parseNumeric(plan?.contract_item_id, 0) || null,
        plan_type: String(plan?.plan_type || "one_time").toLowerCase(),
        status: String(plan?.status || "active").toLowerCase(),
        currency_code: String(plan?.currency_code || this.contract?.currency_code || "MDL").toUpperCase(),
        amount_per_cycle: plan?.amount_per_cycle ?? "",
        total_planned_amount: plan?.total_planned_amount ?? "",
        billing_frequency: String(plan?.billing_frequency || "").toLowerCase(),
        billing_day_of_month: plan?.billing_day_of_month ?? "",
        start_date: toDateInputValue(plan?.start_date),
        end_date: toDateInputValue(plan?.end_date),
        number_of_payments: plan?.number_of_payments ?? "",
        auto_renew: Number(plan?.auto_renew) === 1 || plan?.auto_renew === true,
        next_due_at: toDateTimeInputValue(plan?.next_due_at),
        notes: plan?.notes || "",
      };
      this.billingPlanDialog = true;
    },
    buildBillingPlanPayload() {
      const contractItemId = parseNumeric(this.billingPlanForm.contract_item_id, 0) || null;
      if (!contractItemId) return null;
      const planType = normalizeBillingPlanTypeValue(this.billingPlanForm.plan_type, "one_time");
      const billingFrequency = normalizeBillingFrequencyValue(this.billingPlanForm.billing_frequency, "");
      const nextDueParts = parseDateParts(this.billingPlanForm.next_due_at);
      const payload = {
        contract_item_id: contractItemId,
        plan_type: planType,
        status: String(this.billingPlanForm.status || "").trim().toLowerCase() || "active",
        currency_code: String(this.billingPlanForm.currency_code || this.contract?.currency_code || "MDL")
          .trim()
          .toUpperCase(),
        amount_per_cycle: normalizeMoneyInput(this.billingPlanForm.amount_per_cycle),
        total_planned_amount: normalizeMoneyInput(this.billingPlanForm.total_planned_amount),
        billing_frequency: billingFrequency || null,
        billing_day_of_month: normalizeIntegerInput(this.billingPlanForm.billing_day_of_month),
        start_date: fromDateInputValue(this.billingPlanForm.start_date),
        end_date: fromDateInputValue(this.billingPlanForm.end_date),
        number_of_payments: normalizeIntegerInput(this.billingPlanForm.number_of_payments),
        auto_renew: this.billingPlanForm.auto_renew === true,
        next_due_at: fromDateTimeInputValue(this.billingPlanForm.next_due_at),
        notes: String(this.billingPlanForm.notes || "").trim() || null,
      };
      if (this.isBillingPlanTypeOneTime(planType)) {
        payload.amount_per_cycle = null;
        payload.billing_frequency = "one_time";
        payload.billing_day_of_month = null;
        payload.end_date = null;
        payload.number_of_payments = null;
        payload.auto_renew = false;
      } else if (this.isBillingPlanTypeRecurringLike(planType)) {
        if (!this.isCalendarBillingFrequency(payload.billing_frequency)) {
          payload.billing_day_of_month = null;
        } else if (nextDueParts) {
          payload.billing_day_of_month = nextDueParts.day;
        }
      } else if (this.isBillingPlanTypeInstallment(planType)) {
        if (!this.isCalendarBillingFrequency(payload.billing_frequency)) {
          payload.billing_day_of_month = null;
        } else if (nextDueParts) {
          payload.billing_day_of_month = nextDueParts.day;
        }
      } else if (this.isBillingPlanTypeMilestone(planType)) {
        payload.amount_per_cycle = null;
        payload.billing_frequency = null;
        payload.billing_day_of_month = null;
        payload.end_date = null;
        payload.number_of_payments = null;
        payload.auto_renew = false;
      }
      return payload;
    },
    async submitBillingPlanForm() {
      if (!this.normalizedContractId || !this.canSubmitBillingPlanForm) return;
      const payload = this.buildBillingPlanPayload();
      if (!payload) return;
      this.billingPlanSaving = true;
      try {
        if (this.editingBillingPlanId) {
          await axios.patch(
            `/ncrm/contracts/${this.normalizedContractId}/billing-plans/${this.editingBillingPlanId}`,
            payload
          );
          this.showSnackbar(this.$t("ncrm.contracts.messages.billingPlanUpdated"), "success");
        } else {
          await axios.post(`/ncrm/contracts/${this.normalizedContractId}/billing-plans`, payload);
          this.showSnackbar(this.$t("ncrm.contracts.messages.billingPlanCreated"), "success");
        }
        this.billingPlanDialog = false;
        await this.loadContract();
      } catch (error) {
        console.error("[ncrm-contract-details] save billing plan failed", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contracts.messages.billingPlanSaveError"),
          "error"
        );
      } finally {
        this.billingPlanSaving = false;
      }
    },
    openRecordPaymentDialog(payment = null) {
      this.$refs.paymentDialogRef?.openRecordDialog?.(payment);
    },
    openEditPaymentDialog(payment) {
      this.$refs.paymentDialogRef?.openEditDialog?.(payment);
    },
    handlePaymentDialogNotification(payload = {}) {
      const message = String(payload?.message || "").trim();
      if (!message) return;
      this.showSnackbar(message, payload?.color || "info");
    },
    handleItemFieldsNotification(payload = {}) {
      const message = String(payload?.message || "").trim();
      if (!message) return;
      this.showSnackbar(message, payload?.color || "info");
    },
    openCreateFileDialog() {
      this.editingFileId = null;
      this.fileForm = this.createEmptyFileForm();
      this.fileDialog = true;
    },
    openEditFileDialog(file) {
      this.editingFileId = parseNumeric(file?.id, 0) || null;
      this.fileForm = {
        file: null,
        file_name: String(file?.file_name || "").trim(),
        file_category: String(file?.file_category || "contract_document").toLowerCase(),
        current_stored_path: String(file?.stored_path || "").trim(),
      };
      this.fileDialog = true;
    },
    onFileSelectionChanged(file) {
      const selectedFile = Array.isArray(file) ? file[0] : file;
      if (!selectedFile) return;
      if (!String(this.fileForm.file_name || "").trim()) {
        this.fileForm.file_name = selectedFile.name || "";
      }
    },
    async uploadBinaryFile(file) {
      const selectedFile = Array.isArray(file) ? file[0] : file;
      if (!selectedFile) return null;
      const formData = new FormData();
      formData.append("file", selectedFile);
      const { data } = await axios.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return mapContractFileUpload(data, selectedFile);
    },
    async submitFileForm() {
      if (!this.normalizedContractId || !this.canSubmitFileForm) return;
      this.fileSaving = true;
      try {
        const payload = {
          file_name: String(this.fileForm.file_name || "").trim(),
          file_category: String(this.fileForm.file_category || "contract_document").trim().toLowerCase() || "contract_document",
          is_active: true,
        };
        if (!payload.file_name) {
          this.showSnackbar(this.$t("ncrm.contracts.messages.fileValidationError"), "warning");
          return;
        }
        const uploaded = await this.uploadBinaryFile(this.fileForm.file);
        if (!this.editingFileId) {
          if (!uploaded?.stored_path) {
            this.showSnackbar(this.$t("ncrm.contracts.messages.fileValidationError"), "warning");
            return;
          }
          payload.stored_path = uploaded.stored_path;
          payload.upload_token = uploaded.upload_token;
          payload.mime_type = uploaded.mime_type;
          payload.file_size_bytes = uploaded.file_size_bytes || null;
          if (!payload.file_name && uploaded.file_name) {
            payload.file_name = uploaded.file_name;
          }
          await axios.post(`/ncrm/contracts/${this.normalizedContractId}/files`, payload);
          this.showSnackbar(this.$t("ncrm.contracts.messages.fileCreated"), "success");
        } else {
          if (uploaded?.stored_path) {
            payload.stored_path = uploaded.stored_path;
            payload.upload_token = uploaded.upload_token;
            payload.mime_type = uploaded.mime_type;
            payload.file_size_bytes = uploaded.file_size_bytes || null;
          }
          await axios.patch(`/ncrm/contracts/${this.normalizedContractId}/files/${this.editingFileId}`, payload);
          this.showSnackbar(this.$t("ncrm.contracts.messages.fileUpdated"), "success");
        }
        this.fileDialog = false;
        await this.loadContract();
      } catch (error) {
        console.error("[ncrm-contract-details] save file failed", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contracts.messages.fileSaveError"),
          "error"
        );
      } finally {
        this.fileSaving = false;
      }
    },
    async openContractFile(file) {
      const opened = await openContractFileDownload({
        httpClient: axios,
        browserWindow: typeof window === "undefined" ? null : window,
        contractId: this.normalizedContractId,
        file,
      });
      if (!opened) {
        this.showSnackbar(this.$t("ncrm.contracts.messages.fileOpenError"), "warning");
      }
    },
    openDeleteEntityDialog(type, item) {
      const entityId = parseNumeric(item?.id, 0) || null;
      if (!entityId) return;
      let title = "";
      let hint = "";
      if (type === "billingPlan") {
        title = this.$t("ncrm.contracts.details.deleteDialog.titles.billingPlan");
        hint = this.$t("ncrm.contracts.details.deleteDialog.hints.billingPlan", {
          item: this.contractItemLabel(item?.contract_item_id),
        });
      } else if (type === "payment") {
        title = this.$t("ncrm.contracts.details.deleteDialog.titles.payment");
        hint = this.$t("ncrm.contracts.details.deleteDialog.hints.payment", {
          item: this.paymentDeleteLabel(item),
        });
      } else if (type === "file") {
        title = this.$t("ncrm.contracts.details.deleteDialog.titles.file");
        hint = this.$t("ncrm.contracts.details.deleteDialog.hints.file", {
          item: String(item?.file_name || "").trim() || `#${entityId}`,
        });
      } else {
        return;
      }
      this.deleteDialog = {
        model: true,
        targetType: type,
        targetId: entityId,
        confirmText: "",
        loading: false,
        title,
        hint,
      };
    },
    openDeleteContractDialog() {
      if (!this.normalizedContractId) return;
      const contractLabel = String(this.contract?.contract_number || "").trim()
        || String(this.contract?.name || "").trim()
        || `#${this.normalizedContractId}`;
      this.deleteDialog = {
        model: true,
        targetType: "contract",
        targetId: this.normalizedContractId,
        confirmText: "",
        loading: false,
        title: this.$t("ncrm.contracts.details.deleteDialog.titles.contract"),
        hint: this.$t("ncrm.contracts.details.deleteDialog.hints.contract", {
          item: contractLabel,
        }),
      };
    },
    async confirmDeleteEntity() {
      if (!this.normalizedContractId || !this.deleteDialog.targetType || !this.deleteDialog.targetId) return;
      this.deleteDialog = {
        ...this.deleteDialog,
        loading: true,
      };
      try {
        const wasContractDelete = this.deleteDialog.targetType === "contract";
        if (this.deleteDialog.targetType === "billingPlan") {
          await axios.delete(
            `/ncrm/contracts/${this.normalizedContractId}/billing-plans/${this.deleteDialog.targetId}`
          );
          this.showSnackbar(this.$t("ncrm.contracts.messages.billingPlanDeleted"), "success");
        } else if (this.deleteDialog.targetType === "payment") {
          await axios.delete(`/ncrm/contracts/${this.normalizedContractId}/payments/${this.deleteDialog.targetId}`);
          this.showSnackbar(this.$t("ncrm.contracts.messages.paymentDeleted"), "success");
        } else if (this.deleteDialog.targetType === "file") {
          await axios.delete(`/ncrm/contracts/${this.normalizedContractId}/files/${this.deleteDialog.targetId}`);
          this.showSnackbar(this.$t("ncrm.contracts.messages.fileDeleted"), "success");
        } else if (this.deleteDialog.targetType === "contract") {
          await axios.delete(`/ncrm/contracts/${this.normalizedContractId}`, {
            data: {
              confirmText: this.deleteDialog.confirmText,
            },
          });
          this.showSnackbar(this.$t("ncrm.contracts.messages.contractDeleted"), "success");
        }
        this.deleteDialog = {
          model: false,
          targetType: "",
          targetId: null,
          confirmText: "",
          loading: false,
          title: "",
          hint: "",
        };
        if (wasContractDelete) {
          this.goBack();
          return;
        }
        await this.loadContract();
      } catch (error) {
        console.error("[ncrm-contract-details] delete entity failed", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contracts.messages.deleteError"),
          "error"
        );
        this.deleteDialog = {
          ...this.deleteDialog,
          loading: false,
        };
      }
    },
    async loadContract() {
      if (!this.normalizedContractId) {
        this.contract = null;
        return;
      }
      this.loading = true;
      try {
        const { data } = await axios.get(`/ncrm/contracts/${this.normalizedContractId}`);
        this.contract = data || null;
        this.statusMenu = false;
        if (!(this.contract?.fulfillment_state?.can_mark_fulfilled === true)) {
          this.fulfillmentDialog = false;
        }
        await this.ensureBillingSetupState();
      } catch (error) {
        console.error("[ncrm-contract-details] load failed", error);
        this.contract = null;
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contracts.messages.loadDetailsError"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style scoped src="./NcrmContractDetails.styles.css"></style>
